import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { categories } from '../constants/categories';
import { formatMoney } from '../utils/formatters';
import { SelectField } from '../components/SelectField';
import CurrencySelect from '../components/CurrencySelect';
import LadiesCategoryPicker from '../components/LadiesCategoryPicker';
import ServiceCategoryPicker from '../components/ServiceCategoryPicker';
import { publishAdUnified, HubType } from '../api/ads';
import { trackEvent } from '../utils/analytics';
import { ErrorCodes, toUserError, getApiBase } from '../api/httpClient';
import VerticalAttrsForm, { detectVertical } from '../components/VerticalAttrsForm';
import LocationPickerSheet from '../components/LocationPickerSheet';
import { getCountry, formatLocationLabel } from '../data/locations';
import { getAdFormDefaults, saveAdFormDefaults } from '../utils/adFormDefaults';
import { normalizeInternationalPhone } from '../utils/phone';
// iter334 — Premium currency picker + split phone input.
import CurrencyBottomSheet from '../components/common/CurrencyBottomSheet';
import PhoneInputSplit from '../components/common/PhoneInputSplit';
import '../components/CurrencySelect.css';
import './AddAd.css';
import './AddAd_premium.css';

// Ladies-specific categories (when hub=ladies)
const LADIES_CATEGORIES = [
  { slug: 'henna', nameAr: 'حنّة', nameEn: 'Henna' },
  { slug: 'perfume', nameAr: 'عطور وبخور', nameEn: 'Perfume & Bakhour' },
  { slug: 'toub', nameAr: 'توب سوداني', nameEn: 'Sudanese Toub' },
  { slug: 'gold', nameAr: 'ذهب ومجوهرات', nameEn: 'Gold & Jewelry' },
  { slug: 'african_fabric', nameAr: 'أقمشة أفريقية', nameEn: 'African Fabrics' },
  { slug: 'muslim_wear', nameAr: 'ملابس إسلامية وحجاب', nameEn: 'Muslim Wear & Hijab' },
  { slug: 'dresses', nameAr: 'فساتين', nameEn: 'Dresses' },
  { slug: 'wedding', nameAr: 'فساتين زفاف', nameEn: 'Wedding Dresses' },
  { slug: 'shoes', nameAr: 'أحذية', nameEn: 'Shoes' },
  { slug: 'bags', nameAr: 'شنط', nameEn: 'Bags' },
  { slug: 'makeup', nameAr: 'مكياج', nameEn: 'Makeup' },
  { slug: 'hair', nameAr: 'شعر وعناية', nameEn: 'Hair & Care' },
  { slug: 'skincare', nameAr: 'عناية بالبشرة', nameEn: 'Skincare' },
  { slug: 'underwear', nameAr: 'ملابس داخلية', nameEn: 'Underwear' },
  { slug: 'salons', nameAr: 'صالونات', nameEn: 'Salons & Beauty' },
  { slug: 'home_business', nameAr: 'مشاريع منزلية', nameEn: 'Home Businesses' },
];

// ========================================
// LADIES HUB - Unified title field guidance
// Title = product/service NAME only (short, clear, searchable)
// Details belong in Description field
// ========================================
const LADIES_TITLE_EXAMPLES = {
  default: {
    ar: { placeholder: 'مثال: حذاء نسائي، خدمة حناء، طقم ذهب', helper: 'اكتبي اسمًا قصيرًا وواضحًا فقط' },
    en: { placeholder: 'e.g. Women\'s heels, Henna service, Gold set', helper: 'Use a short, clear name only' }
  }
};

// Category-specific title placeholders for general marketplace
const CATEGORY_TITLE_HINTS = {
  vehicles: {
    ar: 'مثال: تويوتا كورولا، هيونداي توسان، بي ام دبليو X5',
    en: 'e.g. Toyota Corolla, Hyundai Tucson, BMW X5'
  },
  spare_parts: {
    ar: 'مثال: فلتر زيت تويوتا، إطار مقاس 16، بطارية سيارة',
    en: 'e.g. Toyota Oil Filter, 16-inch Tire, Car Battery'
  },
  electronics: {
    ar: 'مثال: تلفزيون سامسونج، سماعات بلوتوث، كاميرا كانون',
    en: 'e.g. Samsung TV, Bluetooth Speaker, Canon Camera'
  },
  mobile_accessories: {
    ar: 'مثال: آيفون 14، سامسونج جالكسي، شاحن لاسلكي',
    en: 'e.g. iPhone 14, Samsung Galaxy, Wireless Charger'
  },
  laptops_accessories: {
    ar: 'مثال: لابتوب HP، ماك بوك برو، ماوس لاسلكي',
    en: 'e.g. HP Laptop, MacBook Pro, Wireless Mouse'
  },
  jobs: {
    ar: 'مثال: محاسب، مدير مبيعات، سائق',
    en: 'e.g. Accountant, Sales Manager, Driver'
  },
  real_estate: {
    ar: 'مثال: شقة غرفتين، فيلا في الخرطوم، أرض تجارية',
    en: 'e.g. 2 Bedroom Apartment, Villa in Khartoum, Commercial Land'
  },
  services: {
    ar: 'مثال: سباكة، إصلاح سيارات، خدمة تنظيف',
    en: 'e.g. Plumbing, Car Repair, Cleaning Service'
  },
  furniture: {
    ar: 'مثال: كنبة جلد، طاولة طعام، سرير مزدوج',
    en: 'e.g. Leather Sofa, Dining Table, Double Bed'
  },
  home: {
    ar: 'مثال: ثلاجة سامسونج، غسالة أوتوماتيك، مكيف سبليت',
    en: 'e.g. Samsung Fridge, Automatic Washer, Split AC'
  },
  apparel: {
    ar: 'مثال: ثوب سوداني، حذاء رياضي، حقيبة يد',
    en: 'e.g. Sudanese Thobe, Sports Shoes, Handbag'
  },
  food: {
    ar: 'مثال: تمور سكري، عسل طبيعي، بهارات',
    en: 'e.g. Dates, Natural Honey, Spices'
  },
  agriculture: {
    ar: 'مثال: بذور قمح، سماد عضوي، جرار زراعي',
    en: 'e.g. Wheat Seeds, Organic Fertilizer, Farm Tractor'
  },
  pets: {
    ar: 'مثال: كلب، قطة، طيور، أسماك',
    en: 'e.g. Dog, Cat, Bird, Fish'
  },
  jewelry: {
    ar: 'مثال: ذهب، فضة، خاتم، سلسلة',
    en: 'e.g. Gold, Silver, Ring, Necklace'
  },
  industrial: {
    ar: 'مثال: مولد كهربائي، ضاغط هواء، ماكينة لحام',
    en: 'e.g. Generator, Air Compressor, Welding Machine'
  },
  equipment: {
    ar: 'مثال: مثقاب كهربائي، منشار، طقم عدة',
    en: 'e.g. Electric Drill, Saw, Tool Kit'
  },
  gifts: {
    ar: 'مثال: مبخرة، سجاد يدوي، صندوق هدايا',
    en: 'e.g. Incense Burner, Handmade Rug, Gift Box'
  },
  metals: {
    ar: 'مثال: حديد تسليح، ألومنيوم، نحاس',
    en: 'e.g. Rebar, Aluminum, Copper'
  },
  fabrics: {
    ar: 'مثال: قماش قطن، حرير، دمور سوداني',
    en: 'e.g. Cotton Fabric, Silk, Sudanese Damour'
  },
  garden: {
    ar: 'مثال: نباتات زينة، أثاث حدائق، نافورة',
    en: 'e.g. Ornamental Plants, Garden Furniture, Fountain'
  },
  it: {
    ar: 'مثال: سيرفر، راوتر، برنامج محاسبة',
    en: 'e.g. Server, Router, Accounting Software'
  },
  manufacturing: {
    ar: 'مثال: ماكينة تعبئة، خط إنتاج، قالب بلاستيك',
    en: 'e.g. Filling Machine, Production Line, Plastic Mold'
  },
  medical: {
    ar: 'مثال: جهاز قياس ضغط، كرسي متحرك، سماعة طبية',
    en: 'e.g. Blood Pressure Monitor, Wheelchair, Stethoscope'
  },
  security: {
    ar: 'مثال: كاميرا مراقبة، جهاز إنذار، قفل ذكي',
    en: 'e.g. Security Camera, Alarm System, Smart Lock'
  },
  energy: {
    ar: 'مثال: لوح شمسي، بطارية ليثيوم، إنفرتر',
    en: 'e.g. Solar Panel, Lithium Battery, Inverter'
  },
  office: {
    ar: 'مثال: طابعة، مكتب خشبي، أدوات مدرسية',
    en: 'e.g. Printer, Wooden Desk, School Supplies'
  },
  beauty: {
    ar: 'مثال: عطر، مكياج، كريم بشرة',
    en: 'e.g. Perfume, Makeup Kit, Skin Cream'
  },
  environment: {
    ar: 'مثال: فلتر مياه، نظام تنقية هواء، سلة إعادة تدوير',
    en: 'e.g. Water Filter, Air Purifier, Recycling Bin'
  },
  commodities: {
    ar: 'مثال: سكر، زيت طعام، صمغ عربي',
    en: 'e.g. Sugar, Cooking Oil, Gum Arabic'
  },
  sports_equipment: {
    ar: 'مثال: دراجة هوائية، جهاز مشي، كرة قدم',
    en: 'e.g. Bicycle, Treadmill, Football'
  },
  games_toys: {
    ar: 'مثال: بلاي ستيشن، لعبة أطفال، بازل',
    en: 'e.g. PlayStation, Kids Toy, Puzzle'
  },
  clothes_shoes: {
    ar: 'مثال: جلابية، حذاء رسمي، عباية',
    en: 'e.g. Jalabiya, Formal Shoes, Abaya'
  },
  health_fitness: {
    ar: 'مثال: جهاز قياس سكر، فيتامينات، حزام ظهر طبي',
    en: 'e.g. Glucose Monitor, Vitamins, Medical Back Belt'
  },
  other: {
    ar: 'مثال: اكتب وصفًا قصيرًا لمنتجك أو خدمتك',
    en: 'e.g. Describe your product or service briefly'
  }
};

// Preview display value translator — maps stored English values to localized display labels
const PREVIEW_VALUE_MAP = {
  // Condition
  'New': { ar: 'جديد' },
  'Used - Like New': { ar: 'مستعمل - كالجديد' },
  'Used - Excellent': { ar: 'مستعمل - ممتاز' },
  'Used - Good': { ar: 'مستعمل - جيد' },
  'Used - Fair': { ar: 'مستعمل - مقبول' },
  'For Parts': { ar: 'للقطع' },
  'Salvage': { ar: 'تالف' },
  // Fuel
  'Petrol': { ar: 'بنزين' },
  'Diesel': { ar: 'ديزل' },
  'Hybrid': { ar: 'هجين' },
  'Electric': { ar: 'كهربائي' },
  // Transmission
  'Automatic': { ar: 'أوتوماتيك' },
  'Manual': { ar: 'عادي' },
  // Body Type
  'Sedan': { ar: 'سيدان' },
  'SUV': { ar: 'دفع رباعي' },
  'Pickup': { ar: 'بيك أب' },
  'Hatchback': { ar: 'هاتشباك' },
  'Van': { ar: 'فان' },
  'Coupe': { ar: 'كوبيه' },
  // Property Type
  'Apartment': { ar: 'شقة' },
  'House': { ar: 'منزل' },
  'Villa': { ar: 'فيلا' },
  'Studio': { ar: 'استوديو' },
  'Land': { ar: 'أرض' },
  'Commercial': { ar: 'تجاري' },
};

// Location Pin Icon
const LocationPinIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Map Icon
const MapIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </svg>
);

// Loading Spinner Icon
const SpinnerIcon = () => (
  <svg className="animate-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Section Header Icons
const CategoryIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const PriceIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
    <path d="M12 18V6"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

const PreviewIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

function AddAd() {
  const { t, i18n } = useTranslation();
  const { lang } = useLanguage();
  const isArabic = i18n.language?.startsWith('ar');
  // Helper: translate stored attribute values for preview display
  const displayVal = (val) => {
    if (!val || !isArabic) return val;
    return PREVIEW_VALUE_MAP[val]?.ar || val;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const [step, setStep] = useState(1);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showTopAlert, setShowTopAlert] = useState(false);
  
  // Detect hub from location state or query params
  const hubFromState = location.state?.hub;
  const hubFromQuery = new URLSearchParams(location.search).get('hub');
  const hub = hubFromState || hubFromQuery || 'general';
  const isLadiesHub = hub === 'ladies';

  // iter288 — Detect "Offer a service" entry. The PostingChooser passes
  // listing_type='service' in location.state. When true:
  //  • Category picker is replaced by ServiceCategoryPicker.
  //  • Vehicle / phone / property attribute forms are hidden.
  //  • Publish payload sets listing_type='service' so the backend
  //    forces category='services' regardless of legacy field values.
  const isService = location.state?.listing_type === 'service';
  
  // Default main category from navigation (when user was browsing a specific tab)
  const defaultMainCategory = location.state?.default_main_category || null;
  
  const [formData, setFormData] = useState({
    title: '',
    category: isService ? 'services' : '',
    price: '',
    discountPercent: '',
    location: '',
    description: '',
    currency: 'SDG',
    // Enhanced location data
    latitude: null,
    longitude: null,
    city: '',
    state: '',
    // Hub context
    hub: hub,
    // Ladies Hub specific fields
    category_main: defaultMainCategory || (isService ? 'services' : ''),
    category_sub: '',
    category_path: '',
    // iter288 — Service-listing flow fields. Only populated when the
    // user enters via "Offer a service".
    listing_type: isService ? 'service' : 'product',
    service_category: '',
    service_subcategory: '',
    price_type: '',
    service_options: {},
    // Seller contact fields (all optional)
    seller_phone: '',
    seller_whatsapp: '',
    seller_messenger: '',
    use_same_for_whatsapp: true,  // Toggle: use phone for WhatsApp
    // iter334 — Split phone input. `phone_country_code` (e.g. "+249")
    // auto-defaults from the ad's selected country; `phone_local` is
    // the subscriber digits the seller types in the local-number
    // field. Backend builds `phone_e164` from these.
    phone_country_code: '',
    phone_local: '',
    category_other: ''  // Follow-up value when category = 'other'
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [verticalAttrs, setVerticalAttrs] = useState({});
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedAdId, setPublishedAdId] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapCoords, setMapCoords] = useState({ lat: '', lng: '' });
  const [locationWarningShown, setLocationWarningShown] = useState(false);
  const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
  // iter322 Phase B — Structured location capture. Replaces the
  // legacy free-form `formData.location` text input as the source of
  // truth. Must be confirmed (locationConfirmed=true) before publish.
  // iter334 — `manualCity` is the optional free-text field the seller
  // can fill below the structured picker when the picker's city list
  // doesn't include their town (Sennar / Atbara / Wad Medani / …).
  const EMPTY_MANUAL_LOC = {
    country: '',
    country_name: '',
    region: '',
    city: '',
    area: '',
    // iter337 — Optional level-5 "Extra location details" free text.
    extra: '',
    manualCity: '',
    latitude: null,
    longitude: null,
    locationSource: null,
    locationConfirmed: false,
    locationLabel: '',
  };
  const [manualLocation, setManualLocation] = useState(EMPTY_MANUAL_LOC);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // iter328 — Seller-provided delivery option. Defaults to "no delivery"
  // so legacy drafts keep working. When `delivery.type === 'paid'` the
  // seller must also fill `fee_amount` + `fee_currency` (validated by
  // validateStep(2) below and by the backend).
  const EMPTY_DELIVERY = {
    available: false,
    type: 'none',                // 'none' | 'free' | 'paid' | 'depends_on_location'
    fee_amount: '',
    fee_currency: 'SDG',         // 'SDG' | 'USD'
    area: '',
    notes: '',
  };
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);

  // iter334 — Country → currency / dial-code map from the backend.
  // Powers the premium currency picker (delivery section) and the
  // PhoneInputSplit component (default country code).
  const [countriesMeta, setCountriesMeta] = useState(null);
  const [deliveryCurrencyOptions, setDeliveryCurrencyOptions] = useState([]);
  const [deliveryCurrencySheetOpen, setDeliveryCurrencySheetOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const API = getApiBase();
        const r = await fetch(`${API}/api/meta/country-currencies`);
        const j = await r.json();
        if (cancelled) return;
        setCountriesMeta(j?.countries || {});
      } catch {
        // Soft-fail; phone defaults to +249 and currency picker
        // falls back to a static list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Whenever the listing's country changes, refresh the delivery
  // currency options (primary country currency + USD) and snap the
  // selected currency to the new default if the seller hadn't
  // manually overridden it.
  useEffect(() => {
    let cancelled = false;
    const iso2 = (manualLocation?.country || '').toUpperCase() || 'SD';
    (async () => {
      try {
        const API = getApiBase();
        const r = await fetch(`${API}/api/meta/country-currencies?country=${iso2}`);
        const j = await r.json();
        if (cancelled) return;
        const opts = (j?.currencies || []).map((c) => ({
          ...c,
          flag: c.flag || (countriesMeta?.[iso2]?.flag || '🏳️'),
        }));
        setDeliveryCurrencyOptions(opts);
        // Auto-default the picked currency to the recommended one
        // unless the seller already picked something in the new list.
        setDelivery((p) => {
          const valid = opts.map((o) => o.code).includes(p.fee_currency);
          if (valid) return p;
          const def = opts.find((o) => o.recommended) || opts[0];
          return { ...p, fee_currency: def ? def.code : 'USD' };
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manualLocation?.country, countriesMeta]);
  
  // Draft detection
  const [existingDrafts, setExistingDrafts] = useState([]);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null); // Track last save time for indicator

  // Helper function to compress image before upload
  const compressImage = (file, maxWidth = 1600, quality = 0.75) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Scale down if larger than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => resolve(blob),
            'image/jpeg',
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Helper function to upload images for draft (returns permanent URLs)
  const uploadDraftImages = async (images) => {
    // iter282 — use the smart API-base resolver so live wassla.tech
    // hits same-origin /api/upload instead of the build-time preview URL.
    const API_URL = getApiBase();
    const uploadedUrls = [];
    
    for (const img of images) {
      // Skip if already uploaded (has a real URL, not blob URL)
      if (img.url && !img.url.startsWith('blob:')) {
        uploadedUrls.push(img.url);
        continue;
      }
      // Also check previewUrl for already-uploaded images from loaded draft
      if (img.previewUrl && !img.previewUrl.startsWith('blob:')) {
        uploadedUrls.push(img.previewUrl);
        continue;
      }
      
      // Need to upload this image
      if (img.file) {
        try {
          // Compress image before upload
          const compressedBlob = await compressImage(img.file);
          const compressedFile = new File([compressedBlob], img.file.name, { type: 'image/jpeg' });
          
          const formDataUpload = new FormData();
          formDataUpload.append('files', compressedFile);
          
          const response = await fetch(`${API_URL}/api/upload`, {
            method: 'POST',
            body: formDataUpload
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.images && data.images.length > 0) {
              uploadedUrls.push(data.images[0]);
            }
          }
        } catch (err) {
          console.error('Failed to upload draft image:', err);
        }
      }
    }
    
    return uploadedUrls;
  };

  // Unified saveDraft function - uploads images first, then saves draft to server
  const saveDraft = async (navigateAfter = true) => {
    setSavingDraft(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        toast.error(t('postAd.loginFirst'));
        setSavingDraft(false);
        return false;
      }
      
      // Upload images first to get permanent URLs
      let imageUrls = [];
      if (selectedImages.length > 0) {
        imageUrls = await uploadDraftImages(selectedImages);
      }
      
      // Prepare draft data with permanent image URLs.
      // iter291 — include EVERY field the form holds so EditAd can
      // re-hydrate 1:1 (category, subcategory, service fields, vehicle
      // attrs, hub fields, etc.). The backend `save_ad_draft` then
      // promotes them into top-level db.ads columns + keeps a copy in
      // the raw `draft_data` blob as a safety net.
      const draftData = {
        title: formData.title,
        category: formData.category,
        subcategory: formData.subcategory || formData.subcategory_id || '',
        subcategory_id: formData.subcategory_id || formData.subcategory || '',
        condition: formData.condition || '',
        price: formData.price,
        price_type: formData.price_type,
        discountPercent: formData.discountPercent,
        location: formData.location,
        description: formData.description,
        currency: formData.currency,
        latitude: formData.latitude,
        longitude: formData.longitude,
        city: formData.city,
        state: formData.state,
        hub: formData.hub,
        visibility: formData.visibility,
        sellerPhone: formData.seller_phone,
        sellerWhatsapp: formData.seller_whatsapp,
        sellerMessenger: formData.seller_messenger,
        sellerEmail: formData.seller_email,
        whatsappSameAsPhone: formData.use_same_for_whatsapp,
        // Vertical-specific blobs.
        car_attrs: formData.car_attrs,
        phone_attrs: formData.phone_attrs,
        property_attrs: formData.property_attrs,
        // Service flow fields.
        listing_type: formData.listing_type || 'product',
        service_category: formData.service_category,
        service_subcategory: formData.service_subcategory,
        service_options: formData.service_options,
        // Ladies Hub / nested category fields.
        category_main: formData.category_main,
        category_sub: formData.category_sub,
        category_path: formData.category_path,
        hub_category_main: formData.hub_category_main,
        hub_category_sub: formData.hub_category_sub,
        brand_type: formData.brand_type,
        // iter322 Phase B — Structured location capture (Country / Region /
        // City / Area + GPS coords + locationConfirmed). Saved so the
        // seller can resume the draft without re-picking the location.
        manualLocation: manualLocation,
        // iter328 — Seller-provided delivery option captured on Step 2.
        delivery: delivery,
        images: imageUrls  // Use permanent URLs
      };
      
      const response = await fetch(`${API_URL}/api/ads/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`
        },
        body: JSON.stringify({
          draft_step: step,
          draft_data: draftData
        })
      });
      
      if (response.ok) {
        toast.success(t('postAd.draftSaved'));
        setDirty(false);
        setDraftSavedAt(new Date()); // Track save time for indicator
        if (navigateAfter) {
          navigate('/my-ads?tab=draft');
        }
        return true;
      } else {
        toast.error(t('postAd.draftSaveFailed'));
        return false;
      }
    } catch (e) {
      console.error('Draft save failed:', e);
      toast.error(t('postAd.draftSaveFailed'));
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  // Scroll to top on step change (Fix: new step always starts at top)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [step]);

  // Check for existing drafts on mount
  useEffect(() => {
    const checkDrafts = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          setLoadingDrafts(false);
          return;
        }
        
        const response = await fetch(`${API_URL}/api/ads/drafts`, {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.drafts && data.drafts.length > 0) {
            setExistingDrafts(data.drafts);
            setShowDraftModal(true);
          }
        }
      } catch (e) {
        console.error('Failed to check drafts:', e);
      } finally {
        setLoadingDrafts(false);
      }
    };
    
    checkDrafts();
  }, []);

  // Non-blocking draft sheet: auto-dismiss after 8s so it never
  // freezes Step 1 on mobile. Continues to be dismissable at any
  // point by clicking Start New Ad / close button.
  useEffect(() => {
    if (!showDraftModal) return undefined;
    const timer = setTimeout(() => setShowDraftModal(false), 8000);
    return () => clearTimeout(timer);
  }, [showDraftModal]);

  // Smart Defaults (silent): prefill category + location from the last
  // successful publish ONLY if the fields are still empty. User input is
  // never overridden. Runs exactly once per mount, as soon as a valid
  // authenticated user is available. Anonymous users are ignored.
  const adDefaultsApplied = useRef(false);
  useEffect(() => {
    if (adDefaultsApplied.current) return;
    const uid = user?.user_id;
    if (!uid) return;
    const defaults = getAdFormDefaults(uid);
    if (!defaults) {
      adDefaultsApplied.current = true;
      return;
    }
    adDefaultsApplied.current = true;
    // Functional update so we only fill empty fields at apply time —
    // any user keystroke between mount and this tick wins.
    setFormData(prev => {
      const nextCategory = !prev.category && defaults.category ? defaults.category : prev.category;
      const nextLocation = !prev.location && defaults.location ? defaults.location : prev.location;
      if (nextCategory === prev.category && nextLocation === prev.location) return prev;
      return { ...prev, category: nextCategory, location: nextLocation };
    });
  }, [user?.user_id]);

  // Load draft data into form
  const loadDraft = (draft) => {
    const draftData = draft.draft_data || draft;
    setFormData({
      title: draftData.title || '',
      category: draftData.category || '',
      price: draftData.price || '',
      discountPercent: draftData.discountPercent || '',
      location: draftData.location || '',
      description: draftData.description || '',
      currency: draftData.currency || 'SDG',
      latitude: draftData.latitude || null,
      longitude: draftData.longitude || null,
      city: draftData.city || '',
      state: draftData.state || '',
      hub: draftData.hub || hub,
      seller_phone: draftData.sellerPhone || draftData.seller_phone || '',
      seller_whatsapp: draftData.sellerWhatsapp || '',
      seller_messenger: draftData.sellerMessenger || '',
      seller_email: draftData.sellerEmail || draftData.seller_email || '',
      use_same_for_whatsapp: draftData.whatsappSameAsPhone !== false
    });

    // iter322 Phase B — Restore structured location from draft so the
    // seller doesn't have to re-confirm country + city on resume.
    if (draftData.manualLocation && draftData.manualLocation.country) {
      setManualLocation({ ...EMPTY_MANUAL_LOC, ...draftData.manualLocation });
    } else if (draftData.country || draftData.city) {
      // Best-effort backfill for drafts saved before Phase B shipped.
      const lang = isArabic ? 'ar' : 'en';
      const cn = getCountry(draftData.country);
      setManualLocation({
        ...EMPTY_MANUAL_LOC,
        country: draftData.country || '',
        country_name: cn ? cn.countryName : (draftData.country_name || ''),
        region: draftData.region || draftData.state || '',
        city: draftData.city || '',
        area: draftData.area || '',
        latitude: draftData.latitude || null,
        longitude: draftData.longitude || null,
        locationConfirmed: false, // force seller to re-confirm.
        locationLabel: formatLocationLabel({
          country: draftData.country, region: draftData.region || draftData.state,
          city: draftData.city, area: draftData.area,
        }, lang),
      });
    }

    // iter328 — Restore delivery choice from draft (or backfill from
    // flat fields for older drafts). Default = "no delivery".
    if (draftData.delivery && typeof draftData.delivery === 'object') {
      setDelivery({ ...EMPTY_DELIVERY, ...draftData.delivery });
    } else if (draftData.delivery_available || draftData.delivery_type) {
      setDelivery({
        ...EMPTY_DELIVERY,
        available: Boolean(draftData.delivery_available),
        type: draftData.delivery_type || (draftData.delivery_available ? 'free' : 'none'),
        fee_amount: draftData.delivery_fee_amount != null ? String(draftData.delivery_fee_amount) : '',
        fee_currency: draftData.delivery_fee_currency || 'SDG',
        area: draftData.delivery_area || '',
        notes: draftData.delivery_notes || '',
      });
    }
    
    // Load images if available
    if (draftData.images && draftData.images.length > 0) {
      setSelectedImages(draftData.images.map((url, idx) => ({
        id: `draft-${idx}`,
        url,
        previewUrl: url
      })));
    }
    
    // Go to the last step they were on
    setStep(draft.draft_step || 1);
    setShowDraftModal(false);
    setDirty(true);
  };

  // ========================================
  // Get category-specific title examples for Ladies Hub
  // ========================================
  const getTitleExamples = useCallback(() => {
    if (isLadiesHub) {
      const category = formData.category?.toLowerCase() || '';
      const langKey = isArabic ? 'ar' : 'en';
      const examples = LADIES_TITLE_EXAMPLES[category] || LADIES_TITLE_EXAMPLES.default;
      return examples[langKey];
    }
    // General marketplace: category-specific hints
    const catKey = formData.category?.toLowerCase() || '';
    const hint = CATEGORY_TITLE_HINTS[catKey];
    if (hint) {
      return { placeholder: isArabic ? hint.ar : hint.en, helper: isArabic ? 'اكتب اسمًا قصيرًا وواضحًا فقط' : 'Use a short, clear name only' };
    }
    return null;
  }, [isLadiesHub, formData.category, isArabic]);

  // ========================================
  // Step Validation — validate required fields and show inline errors
  // ========================================
  const validateStep = useCallback((stepNum) => {
    const errors = {};
    if (stepNum === 1) {
      if (!formData.category && !isLadiesHub) errors.category = true;
      if (isLadiesHub && !formData.category_sub) errors.category_sub = true;
      if (!formData.title?.trim()) errors.title = true;
    } else if (stepNum === 2) {
      if (!formData.currency) errors.currency = true;
      if (!formData.price) errors.price = true;
      // iter322 Phase B — Structured location is required for new
      // ads. iter337 — Strict 4-level required hierarchy:
      //   country, region (State/Region), city (Locality/County/
      //   District), area (City/Town/Village). The level-5 "Extra
      //   location details" stays optional. Each missing level
      //   surfaces the same translated copy: "Please complete the
      //   public location so buyers can find nearby ads."
      if (!manualLocation?.country) errors.country = true;
      if (manualLocation?.country && !manualLocation?.region) errors.region = true;
      if (manualLocation?.country && manualLocation?.region && !manualLocation?.city) errors.city = true;
      if (manualLocation?.country && manualLocation?.region && manualLocation?.city && !manualLocation?.area) errors.area = true;
      if (manualLocation?.country && !manualLocation?.locationConfirmed) {
        errors.location_confirmed = true;
      }
      // iter328 — Delivery validation. The seller must explicitly
      // choose whether delivery is available; if they pick "paid",
      // fee amount + currency are mandatory. Defaulting to
      // available=false (no delivery) counts as a valid choice.
      // iter334 — Allowlist widened to every supported country
      // currency + USD (see backend COUNTRY_CURRENCY_MAP). Validation
      // keeps using the picker payload so it stays in sync.
      if (delivery?.available && delivery?.type === 'paid') {
        const amt = parseFloat(delivery.fee_amount);
        if (!amt || amt <= 0) {
          errors.delivery_fee_amount = true;
        }
        const _i334_codes = ['SDG','EGP','SSP','AED','SAR','QAR','KES','ETB','UGX','TZS','LYD','MAD','USD'];
        if (!delivery.fee_currency || !_i334_codes.includes(delivery.fee_currency)) {
          errors.delivery_fee_currency = true;
        }
      }
    } else if (stepNum === 3) {
      if (!formData.description?.trim()) errors.description = true;
      // iter314 — Contact validation is country-neutral but still
      // strict: at least one of phone / WhatsApp must be a parseable
      // E.164 number (Sudan local format also accepted via the Sudan
      // normalizer). The seller is free to choose phone-only,
      // WhatsApp-only, or both (the toggle wires WhatsApp = phone
      // automatically when checked).
      // iter334 — When the new split-phone fields are present
      // (`phone_country_code` + `phone_local`), validate THEM first;
      // they are the canonical input from the new UI. We also keep
      // backwards-compat with the legacy `seller_phone` full-string
      // field so old drafts still validate.
      //
      // We split the error state into two reasons so the banner can
      // show the right message:
      //   - 'empty'   → no phone AND no WhatsApp supplied
      //   - 'invalid' → at least one was supplied but couldn't be
      //                 parsed as a valid international number
      const splitCC = (formData.phone_country_code || '').trim();
      const splitLocal = (formData.phone_local || '').replace(/\D/g, '').replace(/^0+/, '');
      const splitValid = !!splitCC && splitCC.startsWith('+') && splitLocal.length >= 5 && splitLocal.length <= 15;
      const splitProvided = !!splitCC || !!splitLocal;

      const phoneRaw = (formData.seller_phone || '').trim();
      const waSource = formData.use_same_for_whatsapp
        ? (splitValid ? `${splitCC}${splitLocal}` : formData.seller_phone)
        : formData.seller_whatsapp;
      const waRaw = (waSource || '').trim();
      const phoneNorm = normalizeInternationalPhone(formData.seller_phone);
      const waNorm = normalizeInternationalPhone(waSource);
      const phoneOk = splitValid || phoneNorm.valid;
      const waOk = splitValid || waNorm.valid; // split is shared when "same as phone"

      if (!splitProvided && !phoneRaw && !waRaw) {
        errors.seller_contact = 'empty';
      } else if (!phoneOk && !waOk) {
        errors.seller_contact = 'invalid';
        // Surface the field-level error so the inline message renders
        // beside the split-phone input, not just the top banner.
        if (splitProvided) errors.phone_split = true;
        else if (phoneRaw) errors.seller_phone = true;
      } else {
        // Flag the specific invalid field(s) so the seller sees which
        // input to fix; an empty string is allowed when the *other*
        // method is valid.
        if (!splitValid && splitProvided && !waNorm.valid) {
          errors.phone_split = true;
          errors.seller_contact = 'invalid';
        }
        if (phoneRaw && !phoneNorm.valid && !splitValid) {
          errors.seller_phone = true;
          errors.seller_contact = 'invalid';
        }
        if (!formData.use_same_for_whatsapp && waRaw && !waNorm.valid) {
          errors.seller_whatsapp = true;
          errors.seller_contact = 'invalid';
        }
      }
    }
    return errors;
  }, [formData, isLadiesHub, manualLocation, delivery]);

  const handleValidatedNext = useCallback((currentStep, nextStep) => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setShowTopAlert(true);
      // iter338 — Smooth scroll to the FIRST invalid field, focus the
      // input if it accepts focus. Uses Element.scrollIntoView with
      // block:'center' so the field is mid-viewport regardless of
      // which element is scrolling (window vs. internal flex
      // container). A follow-up scrollBy keeps the field above the
      // BottomNav + orange FAB on short phones.
      setTimeout(() => {
        // Prefer the contact-error banner (it sits right above the
        // phone input on step 3/4); fall back to the first DOM
        // element carrying `.has-error`.
        const errorEl =
          document.querySelector('[data-testid="seller-contact-error"]') ||
          document.querySelector('.has-error');
        if (!errorEl) return;
        try {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          errorEl.scrollIntoView();
        }
        // After the smooth scroll finishes, focus the first focusable
        // input/select/textarea anywhere from the error banner down
        // so the seller can start typing without an extra tap.
        // The banner is rendered ABOVE the phone input, so search
        // forward from the banner using nextElementSibling traversal.
        setTimeout(() => {
          const candidates = Array.from(
            document.querySelectorAll(
              '.has-error input, .has-error select, .has-error textarea, ' +
              '.iter334-phone.has-error input, ' +
              '.iter334-phone.has-error .iter334-phone-codepill',
            ),
          );
          const focusable = candidates.find((el) => !el.disabled);
          if (focusable && typeof focusable.focus === 'function') {
            try { focusable.focus({ preventScroll: true }); } catch { /* noop */ }
          }
        }, 320);
        // Belt-and-braces: ensure the scroll target isn't visually
        // hidden by the bottom nav (~64px) + orange FAB safe gap.
        setTimeout(() => {
          const r = errorEl.getBoundingClientRect();
          const vh = window.innerHeight || 800;
          // If the element bottom is too close to the viewport bottom,
          // nudge upward so the field sits above the bottom chrome.
          if (r.bottom > vh - 96) {
            window.scrollBy({ top: 120, behavior: 'smooth' });
          }
        }, 420);
      }, 80);
      return;
    }
    setFieldErrors({});
    setShowTopAlert(false);
    setStep(nextStep);
  }, [validateStep]);

  // iter338 — Clear stale validation errors WHEN the seller starts
  // editing the relevant fields again. Previously `fieldErrors` and
  // `validateStep` were also in the deps, which created a circular
  // re-render: setFieldErrors(...) → effect runs → setFieldErrors({}).
  // That circular reset is exactly what produced the "phone area
  // blinks/flashes red" UX bug. The watched-fields-only dep list
  // breaks the loop so the red border stays STABLE until the seller
  // actually edits a field.
  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      setFieldErrors({});
      setShowTopAlert(false);
    }
  }, [
    formData.title,
    formData.category,
    formData.category_sub,
    formData.price,
    formData.currency,
    formData.description,
    formData.seller_phone,
    formData.seller_whatsapp,
    formData.phone_country_code,
    formData.phone_local,
    formData.use_same_for_whatsapp,
    manualLocation,
    delivery,
  ]);

  // Cleanup object URLs ONLY on component unmount (not when selectedImages changes)
  // Fix: Store URLs to revoke in a ref to avoid revoking during navigation
  const urlsToCleanupRef = React.useRef([]);
  
  useEffect(() => {
    // Track new URLs for cleanup
    selectedImages.forEach(img => {
      if (img.previewUrl && !urlsToCleanupRef.current.includes(img.previewUrl)) {
        urlsToCleanupRef.current.push(img.previewUrl);
      }
    });
  }, [selectedImages]);
  
  useEffect(() => {
    // Cleanup only on unmount
    return () => {
      urlsToCleanupRef.current.forEach(url => {
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
      });
      urlsToCleanupRef.current = [];
    };
  }, []);

  // Image quality analysis function (client-side, lightweight)
  const analyzeImageQuality = useCallback((imageFile) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Resize for faster analysis
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Calculate brightness
        let totalBrightness = 0;
        let pixelCount = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          totalBrightness += (r + g + b) / 3;
          pixelCount++;
        }
        const avgBrightness = totalBrightness / pixelCount;
        const isDark = avgBrightness < 50; // Too dark threshold
        
        // Simple blur detection using Laplacian variance
        let variance = 0;
        const grayData = [];
        for (let i = 0; i < data.length; i += 4) {
          grayData.push((data[i] + data[i + 1] + data[i + 2]) / 3);
        }
        
        const width = canvas.width;
        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const laplacian = 
              -grayData[idx - width] - grayData[idx - 1] + 
              4 * grayData[idx] - 
              grayData[idx + 1] - grayData[idx + width];
            variance += laplacian * laplacian;
          }
        }
        variance /= (canvas.width * canvas.height);
        const isBlurry = variance < 100; // Low variance = blurry
        
        resolve({
          isDark,
          isBlurry,
          brightness: avgBrightness,
          sharpness: variance,
          hasQualityIssue: isDark || isBlurry
        });
        
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => {
        resolve({ isDark: false, isBlurry: false, hasQualityIssue: false });
      };
      
      img.src = URL.createObjectURL(imageFile);
    });
  }, []);

  const handleExit = () => {
    if (dirty) {
      setShowExitDialog(true);
    } else if (hasInAppHistory()) {
      navigate(-1);
    } else {
      navigate(isLadiesHub ? '/ladies' : '/', { replace: true });
    }
  };

  const confirmExit = () => {
    selectedImages.forEach(img => {
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });
    setShowExitDialog(false);
    setFormData({ title: '', category: '', price: '', discountPercent: '', location: '', description: '', currency: 'SDG' });
    setSelectedImages([]);
    setDirty(false);
    setStep(1);
    if (hasInAppHistory()) navigate(-1);
    else navigate(isLadiesHub ? '/ladies' : '/', { replace: true });
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      if (dirty) {
        setShowExitDialog(true);
      } else if (hasInAppHistory()) {
        navigate(-1);
      } else {
        navigate(isLadiesHub ? '/ladies' : '/', { replace: true });
      }
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'category') setVerticalAttrs({});
    setDirty(true);
  }, []);

  // Get category-specific description example
  const getDescriptionExample = () => {
    const category = formData.category?.toLowerCase() || '';
    
    // For Ladies Hub, provide more relevant examples
    if (isLadiesHub) {
      if (category.includes('shoes') || category.includes('أحذية')) {
        return t('postAd.descHintShoes');
      } else if (category.includes('bags') || category.includes('شنط')) {
        return t('postAd.descHintBags');
      } else if (category.includes('gold') || category.includes('ذهب')) {
        return t('postAd.descHintGold');
      } else if (category.includes('perfume') || category.includes('عطور')) {
        return t('postAd.descHintPerfume');
      }
      return t('postAd.descHintLadiesDefault');
    }
    
    if (category.includes('electronics') || category.includes('إلكترونيات') || category.includes('phones')) {
      return t('postAd.descHintElectronics');
    } else if (category.includes('vehicles') || category.includes('مركبات') || category.includes('cars') || category.includes('auto')) {
      return t('postAd.descHintVehicles');
    } else if (category.includes('real_estate') || category.includes('عقارات')) {
      return t('descExampleRealEstate');
    } else if (category.includes('services') || category.includes('خدمات')) {
      return t('descExampleServices');
    } else if (category.includes('jobs') || category.includes('وظائف')) {
      return t('descExampleJobs');
    } else if (category.includes('fashion') || category.includes('jewelry') || category.includes('أزياء') || category.includes('clothing')) {
      return t('postAd.descHintFashion');
    } else if (category.includes('furniture') || category.includes('أثاث') || category.includes('home')) {
      return t('postAd.descHintFurniture');
    }
    
    return t('postAd.descHintDefault');
  };

  // ========================================
  // LOCATION PERMISSION - Show pre-permission modal for Arabic users
  // ========================================
  const handleLocationButtonClick = () => {
    if (!navigator.geolocation) {
      toast.error(t('postAd.locationNotAvailableBrowser'));
      return;
    }
    // Show pre-permission modal to explain why we need location
    setShowLocationPermissionModal(true);
  };

  const handleLocationPermissionContinue = () => {
    setShowLocationPermissionModal(false);
    handleGetCurrentLocation();
  };

  const handleLocationPermissionCancel = () => {
    setShowLocationPermissionModal(false);
  };

  // Get user's current location using Geolocation API with full city/state extraction
  const handleGetCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      toast.error(t('postAd.locationNotAvailable'));
      return;
    }

    setGettingLocation(true);
    setLocationWarningShown(false);
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      
      // Try to get address from coordinates using reverse geocoding
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
          { headers: { 'Accept-Language': isArabic ? 'ar' : 'en' } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const address = data.address;
          
          // Extract city, state, and build readable address
          const city = address.city || address.town || address.village || address.suburb || '';
          const state = address.state || address.region || '';
          const landmark = address.road || address.neighbourhood || '';
          
          // Build a readable location text: "City, State (near landmark)"
          const parts = [];
          if (city) parts.push(city);
          if (state && state !== city) parts.push(state);
          
          let locationText = parts.length > 0 ? parts.join(', ') : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          if (landmark && parts.length > 0) {
            locationText += ` ${t('postAd.nearLandmark', { landmark })}`;
          }
          
          // Update form with full location data
          setFormData(prev => ({
            ...prev,
            location: locationText,
            latitude: latitude,
            longitude: longitude,
            city: city,
            state: state
          }));
          setDirty(true);
          toast.success(t('postAd.locationFound'));
        } else {
          // Fallback - show warning that only coordinates were saved
          setFormData(prev => ({
            ...prev,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            latitude: latitude,
            longitude: longitude,
            city: '',
            state: ''
          }));
          setDirty(true);
          toast.warning(t('postAd.coordsSaved'));
        }
      } catch {
        // Fallback to coordinates if reverse geocoding fails
        setFormData(prev => ({
          ...prev,
          location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          latitude: latitude,
          longitude: longitude,
          city: '',
          state: ''
        }));
        setDirty(true);
      toast.warning(t('postAd.coordsSaved'));
      }
    } catch (error) {
      // Show localized error messages
      if (error.code === 1) {
        // Permission denied
        toast.error(t('postAd.locationPermDenied'));
      } else if (error.code === 2) {
        // Position unavailable
        toast.error(t('postAd.locationUnavailable'));
      } else if (error.code === 3) {
        // Timeout
        toast.error(t('postAd.locationTimeoutMsg'));
      } else {
        toast.error(t('postAd.locationErrorMsg'));
      }
      // User can still enter manually
    } finally {
      setGettingLocation(false);
    }
  }, [t, isArabic]);

  // Handle map picker confirm with coordinates storage
  const handleMapPickerConfirm = () => {
    const { lat, lng } = mapCoords;
    if (lat && lng) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      setFormData(prev => ({
        ...prev,
        location: `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`,
        latitude: latNum,
        longitude: lngNum
      }));
      setDirty(true);
      setShowMapPicker(false);
      setMapCoords({ lat: '', lng: '' });
      toast.success(t('locationFound') || 'Location set!');
    } else {
      toast.error(t('locationError') || 'Please enter valid coordinates');
    }
  };

  // Handle file selection with image quality analysis
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    const valid = files.filter(f => allowed.includes(f.type));
    const remaining = 5 - selectedImages.length;
    const slice = valid.slice(0, Math.max(0, remaining));
    
    // Analyze each image for quality issues
    const newItems = await Promise.all(slice.map(async (file) => {
      const quality = await analyzeImageQuality(file);
      return {
        file,
        previewUrl: URL.createObjectURL(file),
        qualityIssue: quality.hasQualityIssue,
        isDark: quality.isDark,
        isBlurry: quality.isBlurry
      };
    }));
    if (newItems.length > 0) {
      setSelectedImages(prev => [...prev, ...newItems]);
      setDirty(true);
    }
    e.target.value = '';
  };

  const removeSelectedImage = (index) => {
    setSelectedImages(prev => {
      const item = prev[index];
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
    setDirty(true);
  };

  // Move image up in order (closer to cover position)
  const moveImageUp = (index) => {
    if (index === 0) return;
    setSelectedImages(prev => {
      const newImages = [...prev];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      return newImages;
    });
    setDirty(true);
  };

  // Move image down in order
  const moveImageDown = (index) => {
    if (index === selectedImages.length - 1) return;
    setSelectedImages(prev => {
      const newImages = [...prev];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      return newImages;
    });
    setDirty(true);
  };

  // Count low quality images for moderation
  const getLowQualityImageCount = () => {
    return selectedImages.filter(img => img.qualityIssue).length;
  };

  // Deduplicate images for preview step - prevent duplicates from showing
  const uniquePreviewImages = useMemo(() => {
    const seen = new Set();
    return selectedImages.filter(img => {
      const key = img?.previewUrl || img?.url || img?.file?.name || JSON.stringify(img);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [selectedImages]);

  const handleSubmit = async () => {
    setLoading(true);
    setUploading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Calculate low quality image count for backend moderation
      const lowQualityCount = getLowQualityImageCount();
      
      // Determine hub - 'ladies' or 'main'
      const hub = isLadiesHub ? HubType.LADIES : HubType.MAIN;
      
      // Prepare draft data
      // iter322 Phase B — Compose the legacy `location` text from the
      // structured pick + optional landmark. Backend stores both:
      //   • `location` (free-form display string) — kept for legacy
      //     ad cards and notifications.
      //   • `country`, `country_name`, `region`, `city`, `area`,
      //     `location_label`, `location_confirmed` — strict filtering.
      const structuredLabel = manualLocation?.locationLabel || formatLocationLabel(manualLocation || {}, isArabic ? 'ar' : 'en');
      const landmark = (formData.location || '').trim();
      const composedLocation = [structuredLabel, landmark].filter(Boolean).join(' — ');
      const countryNode = getCountry(manualLocation?.country);

      const draft = {
        title: formData.title,
        category: formData.category,
        price: formData.price,
        discountPercent: formData.discountPercent,
        location: composedLocation || structuredLabel || landmark,
        description: formData.description,
        currency: formData.currency,
        latitude: manualLocation?.latitude ?? formData.latitude,
        longitude: manualLocation?.longitude ?? formData.longitude,
        city: manualLocation?.city || formData.city,
        state: manualLocation?.region || formData.state,
        // iter322 Phase B — Structured location capture (required).
        country: manualLocation?.country || null,
        country_name: manualLocation?.country_name || (countryNode ? countryNode.countryName : null),
        region: manualLocation?.region || null,
        area: manualLocation?.area || null,
        location_label: structuredLabel || null,
        location_confirmed: Boolean(manualLocation?.locationConfirmed),
        // iter334 — Optional free-text city + raw GPS coords from the
        // "Use my current location" picker. Backend stores them for
        // display only; strict filtering still uses the structured
        // `city` field when present.
        location_manual_city: (manualLocation?.manualCity || '').trim() || null,
        location_lat: manualLocation?.latitude ?? null,
        location_lng: manualLocation?.longitude ?? null,
        // iter337 — Level-5 OPTIONAL "Extra location details" free
        // text (neighborhood/market/landmark). Display only.
        location_extra: (manualLocation?.extra || '').trim() || null,
        // iter328 — Seller-provided delivery option. Defaults to
        // available=false / type=none so legacy clients keep working.
        delivery_available: Boolean(delivery?.available),
        delivery_type: delivery?.available ? (delivery.type || 'free') : 'none',
        delivery_fee_amount: (delivery?.available && delivery?.type === 'paid' && delivery.fee_amount)
          ? Number(delivery.fee_amount)
          : null,
        delivery_fee_currency: (delivery?.available && delivery?.type === 'paid')
          ? (delivery.fee_currency || 'SDG')
          : null,
        delivery_area: (delivery?.available ? (delivery.area || '').trim() : '') || null,
        delivery_notes: (delivery?.available ? (delivery.notes || '').trim() : '') || null,
        // Ladies Hub specific
        category_main: formData.category_main || null,
        category_sub: formData.category_sub || null,
        category_path: formData.category_path || null,
        category_other: formData.category_other || null,
        // Seller contact
        // iter314 — Send canonical E.164 ("+249XXXXXXXXX" or any
        // multi-country form) to the backend so the stored record is
        // in lock-step with the WhatsApp deep-link expectation. Sudan
        // local formats still auto-prefix to +249 via the canonical
        // normalizer. Non-parseable inputs fall back to the raw value
        // (forward-compat — the validateStep gate already blocks
        // publishing for invalid contacts).
        seller_phone: (() => {
          // iter334 — Prefer the split inputs when present.
          if (formData.phone_country_code && formData.phone_local) {
            const cc = formData.phone_country_code.startsWith('+')
              ? formData.phone_country_code
              : `+${formData.phone_country_code}`;
            const local = String(formData.phone_local).replace(/\D/g, '').replace(/^0+/, '');
            if (local.length >= 5) return `${cc}${local}`;
          }
          const n = normalizeInternationalPhone(formData.seller_phone);
          return n.valid ? n.e164 : (formData.seller_phone || null);
        })(),
        // iter334 — Backend will also receive split fields and persist
        // phone_e164. Sending them explicitly keeps the contract clear.
        phone_country_code: formData.phone_country_code || null,
        phone_local: formData.phone_local || null,
        seller_whatsapp: (() => {
          const src = formData.use_same_for_whatsapp
            ? formData.seller_phone
            : formData.seller_whatsapp;
          const n = normalizeInternationalPhone(src);
          return n.valid ? n.e164 : (src || null);
        })(),
        seller_messenger: formData.seller_messenger || null,
        use_same_for_whatsapp: formData.use_same_for_whatsapp,
        // Quality flag
        low_quality_image_count: lowQualityCount,
        // Vertical engine attrs
        ...(verticalAttrs.phone_attrs ? { phone_attrs: verticalAttrs.phone_attrs } : {}),
        ...(verticalAttrs.car_attrs ? { car_attrs: verticalAttrs.car_attrs } : {}),
        ...(verticalAttrs.property_attrs ? { property_attrs: verticalAttrs.property_attrs } : {}),
        condition: verticalAttrs.phone_attrs?.condition || verticalAttrs.car_attrs?.condition || null,
        // iter288 — Service-listing fields. Sent only when the user
        // came in via "Offer a service". The backend hard-enforces
        // category='services' when listing_type='service', so even if
        // a buggy client left a stale category in place it can't
        // pollute the product taxonomy.
        listing_type: formData.listing_type || 'product',
        ...(formData.listing_type === 'service' ? {
          service_category: formData.service_category || null,
          service_subcategory: formData.service_subcategory || null,
          price_type: formData.price_type || null,
          service_options: formData.service_options && Object.keys(formData.service_options).length > 0
            ? formData.service_options
            : null,
        } : {}),
      };
      
      // Quality check before publishing
      let qualityData = { publishable: true, warnings: false };
      try {
        const qualityRes = await fetch(`${API_URL}/api/ads/quality-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...draft,
            images: selectedImages.map(img => img.name || 'image'),
            user_id: user?.uid || user?.id,
          }),
        });
        qualityData = await qualityRes.json();
      } catch (qErr) {
        console.warn('Quality check failed (offline?), proceeding:', qErr.message);
      }
      
      // Block extremely poor listings
      if (!qualityData.publishable) {
        setLoading(false);
        setUploading(false);
        const issueMessages = {
          title_too_short: t('postAd.qualityTitleShort'),
          description_too_short: t('postAd.qualityDescShort'),
          no_images: t('postAd.qualityNoImages'),
          no_location: t('postAd.qualityNoLocation'),
          no_price: t('postAd.qualityNoPrice'),
          no_category: t('postAd.qualityNoCategory'),
        };
        const firstIssue = qualityData.issues?.[0];
        const msg = issueMessages[firstIssue] || (t('postAd.qualityImprove'));
        toast.error(msg);
        return;
      }
      
      // Warn on low quality but allow publishing
      if (qualityData.warnings) {
        console.log('Quality warnings:', qualityData.issues);
      }
      
      // Duplicate warning (non-blocking)
      if (qualityData.duplicate_check?.is_duplicate) {
        toast.info(t('postAd.duplicateWarning'));
      }

      // Spam check
      let spamData = { action: 'allow', rate_limit: {} };
      try {
        const spamRes = await fetch(`${API_URL}/api/ads/spam-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...draft,
            user_id: user?.uid || user?.id,
          }),
        });
        spamData = await spamRes.json();
      } catch (sErr) {
        console.warn('Spam check failed (offline?), proceeding:', sErr.message);
      }
      
      // Block spam
      if (spamData.action === 'block') {
        setLoading(false);
        setUploading(false);
        toast.error(t('postAd.spamRejected'));
        return;
      }
      
      // Rate limit check
      if (spamData.rate_limit?.posts_last_10min >= 3) {
        setLoading(false);
        setUploading(false);
        toast.error(t('postAd.rateLimited'));
        return;
      }

      // Use unified publish (handles upload + create atomically)
      const result = await publishAdUnified({
        draft,
        images: selectedImages,
        hub
      });
      
      // Success!
      setPublishedAdId(result.ad_id);
      trackEvent('ad_publish', { ad_id: result.ad_id, category: draft.category }, user?.user_id);
      // Smart Defaults (silent): remember last-used category + location
      // for this user. No-op for anonymous users. Only real publish
      // signals are stored (not abandoned drafts).
      saveAdFormDefaults(
        { category: draft.category, location: draft.location },
        user?.user_id
      );
      selectedImages.forEach(img => {
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      });
      setDirty(false);
      
      // Show hub-specific success message
      if (isLadiesHub) {
        toast.success(t('postAd.publishedToLadiesHub'));
      } else {
        toast.success(t('toast.adPublished'));
      }
      
      // Navigate to premium success screen
      const hubParam = isLadiesHub ? 'ladies' : 'main';
      navigate(`/publish/success?adId=${result.ad_id}&hub=${hubParam}`);
      
    } catch (error) {
      console.error('Publish failed:', error);
      
      // Network / connectivity error — no status means fetch itself failed
      const isOffline = !navigator.onLine || error.status === 0 || error.message === 'Failed to fetch' || error.message?.includes('Network error');
      
      if (isOffline) {
        // Save draft locally before showing error
        try { await saveDraft(); } catch (e) { /* best effort */ }
        if (error.stage === 'upload') {
          // iter282 — surface the real upload failure message so the
          // user sees whether it's network / cloudinary / size etc.
          toast.error(error.message || t('network.uploadFailed'));
        } else {
          toast.error(t('network.publishFailed'));
        }
      } else if (error.status === 401) {
        toast.error(t('postAd.sessionExpired'));
      } else if (error.status === 403) {
        const isBanned = error.code === 'ACCOUNT_BANNED' || 
                         error.code === 'ERR_BANNED_USER' ||
                         error.message?.includes('banned');
        if (isBanned) {
          toast.error(t('postAd.accountBanned'));
        } else {
          toast.error(t('postAd.accessDenied'));
        }
      } else if (error.status === 429 || error.code === ErrorCodes.RATE_LIMITED) {
        toast.error(t('postAd.dailyLimitReached'));
      } else if (error.stage === 'upload') {
        // iter282 — for non-offline upload-stage failures (e.g. backend
        // 400 with detailed body), show the actual error message rather
        // than the generic localized fallback.
        toast.error(error.message || t('network.uploadFailed'));
      } else {
        toast.error(error.message || (t('postAd.publishFailed')));
      }
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const getStepTitle = () => {
    switch(step) {
      case 1: return t('step1Title');
      case 2: return t('step2Title');
      case 3: return t('step3Title');
      case 4: return t('step4Title');
      default: return '';
    }
  };

  if (published) {
    return (
      <div className="add-ad-page">
        <div className="form-wrap">
          <div className="success-container">
            <div className="success-icon">✅</div>
            <h2 className="success-title" data-testid="publish-success-title">
              {t('postAd.published')} ✅
            </h2>
            <p className="success-description">
              {t('postAd.adLiveDesc')}
            </p>
            {isLadiesHub && (
              <p className="success-hub-note">
                {t('postAd.publishedToLadies')}
              </p>
            )}
            <div className="success-buttons">
              <button
                onClick={() => navigate(`/ad/${publishedAdId}`)}
                className="btn btn-primary"
                data-testid="view-ad-button"
              >
                {t('viewAdBtn')}
              </button>
              <button
                onClick={() => isLadiesHub ? navigate('/ladies') : navigate('/account')}
                className="btn btn-secondary"
                data-testid="my-ads-button"
              >
                {isLadiesHub 
                  ? (t('postAd.backToLadiesHub'))
                  : t('myAdsBtn')}
              </button>
              <button
                onClick={() => {
                  setPublished(false);
                  setStep(1);
                  setFormData({ 
                    title: '', category: '', price: '', discountPercent: '', 
                    location: '', description: '', currency: 'SDG',
                    latitude: null, longitude: null, city: '', state: '', hub: hub,
                    seller_phone: '', seller_whatsapp: '', seller_messenger: '', seller_email: '',
                    use_same_for_whatsapp: true
                  });
                  setSelectedImages([]);
                  setUploadedUrls([]);
                  setDirty(false);
                }}
                className="btn btn-ghost"
                data-testid="add-another-button"
              >
                {t('addAdStartBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-ad-page">
      {/* Continue Draft — non-blocking bottom-sheet. Auto-dismisses
          after 8s (see timer below) so it never freezes Step 1. Form
          behind remains fully interactive via pointer-events:none on
          the host, pointer-events:auto on the card. */}
      {showDraftModal && existingDrafts.length > 0 && (
        <div className="draft-modal-host" data-testid="draft-modal-host">
          <div className="draft-modal draft-modal--sheet">
            <button
              type="button"
              onClick={() => setShowDraftModal(false)}
              className="draft-modal-close"
              aria-label={t('close') || 'Close'}
              data-testid="draft-modal-close"
            >
              ×
            </button>
            <h3 className="exit-dialog-title">
              {t('postAd.continueDraftTitle')}
            </h3>
            <p className="exit-dialog-message">
              {t('postAd.draftCountMsg', { count: existingDrafts.length })}
            </p>

            {/* Show most recent draft */}
            <div className="draft-preview-card">
              <div className="draft-preview-title">
                {existingDrafts[0].title || (t('postAd.draftListing'))}
              </div>
              <div className="draft-preview-meta">
                {t('postAd.draftStepOf', { current: existingDrafts[0].draft_step || 1, total: 4 })}
                {existingDrafts[0].draft_data?.images?.length > 0 && (
                  <span className="draft-photos-badge">
                    📷 {existingDrafts[0].draft_data.images.length}
                  </span>
                )}
              </div>
            </div>

            <div className="exit-dialog-buttons-stack draft-buttons-stack">
              {/* Primary: Continue Draft */}
              <button
                onClick={() => loadDraft(existingDrafts[0])}
                className="exit-btn exit-btn-primary"
                data-testid="continue-draft-btn"
              >
                {t('postAd.continueDraftBtn')}
              </button>

              {/* Secondary: View All Drafts */}
              <button
                onClick={() => {
                  setShowDraftModal(false);
                  navigate('/my-ads?tab=draft');
                }}
                className="exit-btn exit-btn-outline"
                data-testid="view-drafts-btn"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginInlineEnd: '6px' }}>
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('postAd.viewDrafts')}
              </button>

              {/* Tertiary/Ghost: Start New Ad */}
              <button
                onClick={() => setShowDraftModal(false)}
                className="exit-btn exit-btn-ghost"
                data-testid="start-new-ad-btn"
              >
                {t('postAd.startNewAd')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Green curved header background */}
      <div className="add-ad-curved-bg">
        {/* Top action buttons */}
        <div className="add-ad-header">
          <button
            onClick={handleBack}
            className="header-back-btn"
            data-testid="back-button"
            aria-label={t('back')}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={handleExit}
            className="header-exit-btn"
            data-testid="exit-add-ad"
            aria-label={t('exitConfirm')}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Premium Title Section - with safe pb-16 spacing */}
        <div className="premium-header-content">
          <div className="step-label">
            {t('postAd.stepOf', { current: step, total: 4 })}
            <span className="posting-time-hint">
              {step >= 3 ? t('postAd.almostDone') : t('postAd.estimatedTime')}
            </span>
          </div>
          <h1 className="premium-title">
            {t('postAd.createYourListing')}
          </h1>
          <p className="premium-subtitle">
            {t('postAd.accurateDetails')}
          </p>
        </div>

        {/* White curved separator - positioned at bottom, lower curve */}
        <div className="curve-separator" aria-hidden="true">
          <svg
            className="curve-svg"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              d="M0,80 C360,110 720,120 1080,110 C1260,104 1380,90 1440,80 L1440,120 L0,120 Z"
            />
          </svg>
        </div>

        {/* Subtle highlight overlay */}
        <div className="header-highlight" />
      </div>

      {/* ✅ Floating card content (over curve) */}
      <div className="add-ad-content">
        <div className="form-wrap">
        {/* Floating Card Container */}
        <div className="card floating-card">
          {/* Apple-level Step Progress */}
          <div className="premium-progress-wrapper">
            <div className="premium-progress-bar">
              <div 
                className="premium-progress-fill" 
                style={{ width: `${((step - 1) / 3) * 100}%` }}
              />
            </div>
            <div className="premium-steps-row">
              {[
                { num: 1, label: t('postAd.stepDetails') },
                { num: 2, label: t('postAd.stepPrice') },
                { num: 3, label: t('postAd.stepPhotos') },
                { num: 4, label: t('postAd.stepReview') }
              ].map(({ num, label }) => {
                const isCompleted = step > num;
                const isCurrent = step === num;
                const canNavigate = isCompleted; // Only allow clicking completed steps
                
                return (
                  <div 
                    key={num} 
                    className={`premium-step-item ${canNavigate ? 'clickable' : ''}`}
                    onClick={() => canNavigate && setStep(num)}
                    role={canNavigate ? 'button' : undefined}
                    tabIndex={canNavigate ? 0 : undefined}
                    onKeyDown={(e) => canNavigate && e.key === 'Enter' && setStep(num)}
                    data-testid={`step-${num}`}
                  >
                    <div className={`premium-step-dot ${isCompleted ? 'done' : ''} ${isCurrent ? 'active' : ''}`}>
                      {isCompleted ? '✓' : num}
                    </div>
                    <div className="premium-step-label">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 1: Category & Title */}
          {step === 1 && (
            <div className="step-content">
              {/* Ladies Hub indicator - compact */}
              {isLadiesHub && (
                <div className="hub-indicator ladies-hub-indicator" data-testid="ladies-hub-indicator">
                  <span className="hub-icon">✨</span>
                  <span className="hub-label">{t('postAd.postingInLadiesHub')}</span>
                </div>
              )}

              {/* Section Header - Direct to action */}
              <div className="section-header">
                <CategoryIcon />
                <span>{t('step1Title')}</span>
              </div>

              {/* Validation Alert */}
              {showTopAlert && step === 1 && (
                <div className="validation-top-alert" data-testid="validation-alert-step1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{isArabic ? 'يوجد حقول مطلوبة لم يتم إكمالها' : 'Some required fields are still missing'}</span>
                </div>
              )}

              {/* Category Picker - Different UI for Ladies Hub vs Services vs Products */}
              {isService ? (
                <div className="row" data-testid="service-flow-picker-row">
                  <label className="label">
                    {isArabic ? 'فئة الخدمة' : 'Service category'} *
                  </label>
                  <ServiceCategoryPicker
                    isArabic={isArabic}
                    value={{
                      categoryId: formData.service_category,
                      subcategoryId: formData.service_subcategory,
                    }}
                    onChange={(sel) => {
                      if (sel) {
                        setFormData(prev => ({
                          ...prev,
                          service_category: sel.categoryId || '',
                          service_subcategory: sel.subcategoryId || '',
                          // Keep canonical product fields stable so the
                          // rest of the form doesn't choke on empty strings.
                          category: 'services',
                          category_main: 'services',
                          category_sub: sel.subcategoryId || '',
                          category_path: sel.categoryId
                            ? `services/${sel.categoryId}${sel.subcategoryId ? '/' + sel.subcategoryId : ''}`
                            : 'services',
                        }));
                        setDirty(true);
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          service_category: '',
                          service_subcategory: '',
                          category_sub: '',
                          category_path: 'services',
                        }));
                      }
                    }}
                  />
                </div>
              ) : isLadiesHub ? (
                <div className="row">
                  <label className="label">{t('categoryLabel')} *</label>
                  <LadiesCategoryPicker
                    value={{
                      mainId: formData.category_main,
                      subId: formData.category_sub,
                      categoryPath: formData.category_path
                    }}
                    onChange={(selection) => {
                      if (selection) {
                        setFormData(prev => ({
                          ...prev,
                          category: selection.categoryPath, // For backward compatibility
                          category_main: selection.mainId,
                          category_sub: selection.subId,
                          category_path: selection.categoryPath
                        }));
                        setDirty(true);
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          category: '',
                          category_main: '',
                          category_sub: '',
                          category_path: ''
                        }));
                      }
                    }}
                    lang={isArabic ? 'ar' : 'en'}
                    isRTL={document.dir === 'rtl' || isArabic}
                    defaultMainCategory={defaultMainCategory}
                  />
                </div>
              ) : (
                <div className="row">
                  <SelectField
                    label={`${t('categoryLabel')} *`}
                    value={formData.category}
                    onChange={(val) => handleFieldChange('category', val)}
                    options={[
                      ...categories.map(cat => ({
                        value: cat.key,
                        label: cat.en,
                        label_ar: cat.ar,
                        icon: cat.icon,
                        // Pass through aliases so the shared option-search
                        // utility matches EN/AR keywords like "phone",
                        // "mobile", "هاتف", "ذهب", etc.
                        aliases: cat.aliases
                      })),
                      { value: 'other', label: 'Other', label_ar: 'أخرى' }
                    ]}
                    placeholder={t('categoryPlaceholder')}
                    title={t('selectCategory')}
                    data-testid="category-select"
                  />
                  {formData.category === 'other' && (
                    <input
                      type="text"
                      value={formData.category_other || ''}
                      onChange={(e) => handleFieldChange('category_other', e.target.value)}
                      className="input"
                      placeholder={isArabic ? 'يرجى تحديد القسم' : 'Please specify category'}
                      data-testid="category-other-input"
                      dir={isArabic ? 'rtl' : 'ltr'}
                      style={{ marginTop: 8, borderColor: '#fde68a', background: '#fffef5' }}
                    />
                  )}
                </div>
              )}

              <div className="row">
                <label className="label">
                  {/* iter289 — Service flow renames the field to
                      "Service Name" to remove the misleading "Product"
                      word that operator reported on live. */}
                  {isService
                    ? (isArabic ? 'اسم الخدمة *' : 'Service Name *')
                    : t('titleLabel')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  className={`input ${fieldErrors.title ? 'has-error' : ''}`}
                  maxLength={80}
                  placeholder={
                    isService
                      ? (isArabic
                          ? 'مثال: صيانة سيارات، تنظيف، مدرس خاص'
                          : 'e.g. Car repair, Cleaning service, Private tutor')
                      : (getTitleExamples()?.placeholder || t('titlePlaceholder'))
                  }
                  data-testid="title-input"
                  dir={isArabic ? 'rtl' : 'ltr'}
                />
                {fieldErrors.title && (
                  <div className="field-error-msg" data-testid="title-error">{isArabic ? 'هذا الحقل مطلوب' : 'This field is required'}</div>
                )}
                <div className="title-field-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.35rem' }}>
                  <p className="helper-text" style={{ margin: 0 }}>{getTitleExamples()?.helper || t('titleHelper')}</p>
                  <span className="char-counter" data-testid="title-char-counter" style={{ fontSize: '0.75rem', color: (formData.title?.length || 0) > 60 ? '#e67e22' : '#999', flexShrink: 0 }}>
                    {formData.title?.length || 0}/80
                  </span>
                </div>
              </div>

              {/* Vertical-specific attributes (Phone, Car, Real Estate) */}
              {/* iter268: pass subcategory so the gating is strict
                  (phone form only when sub is smartphones/tablets/phone_
                  accessories OR parent is mobile_accessories). The
                  onChange handler ONLY updates verticalAttrs — it
                  cannot mutate formData.category, eliminating the
                  "saved as Cars instead of Vehicles" class of bugs. */}
              {!isLadiesHub && !isService && formData.category && (
                <VerticalAttrsForm
                  category={formData.category}
                  subcategory={formData.category_sub}
                  attrs={verticalAttrs}
                  onChange={(v) => { setVerticalAttrs(v); setDirty(true); }}
                  isArabic={isArabic}
                />
              )}

              {/* iter288 — Service-listing optional fields. Rendered ONLY
                  when the user came in via "Offer a service". Replaces
                  the product VerticalAttrsForm so a car-mechanic service
                  is NEVER asked for car brand/model. All fields are
                  OPTIONAL per the operator spec. */}
              {isService && formData.service_subcategory && (
                <div className="vertical-attrs-section" data-testid="service-details-form">
                  <div className="vertical-attrs-header">
                    <span className="vertical-attrs-title">
                      {isArabic ? 'تفاصيل الخدمة (اختياري)' : 'Service details (optional)'}
                    </span>
                  </div>

                  {/* Price type — fixed / starting_from / negotiable / free_consultation */}
                  <div className="row">
                    <label className="label">
                      {isArabic ? 'نوع السعر' : 'Price type'}
                    </label>
                    <div className="chip-group" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {[
                        { id: 'fixed',             en: 'Fixed price',       ar: 'سعر ثابت' },
                        { id: 'starting_from',     en: 'Starting from',     ar: 'ابتداءً من' },
                        { id: 'negotiable',        en: 'Negotiable',        ar: 'قابل للتفاوض' },
                        { id: 'free_consultation', en: 'Free consultation', ar: 'استشارة مجانية' },
                      ].map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className={`chip ${formData.price_type === p.id ? 'chip-active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, price_type: p.id }))}
                          data-testid={`service-price-type-${p.id}`}
                          style={{
                            border: '1px solid var(--border-color, #e5e7eb)',
                            background: formData.price_type === p.id ? 'rgba(37,99,235,0.12)' : '#fff',
                            borderColor: formData.price_type === p.id ? '#2563eb' : 'var(--border-color, #e5e7eb)',
                            color: formData.price_type === p.id ? '#2563eb' : 'inherit',
                            borderRadius: 999,
                            padding: '6px 12px',
                            fontSize: 13,
                            cursor: 'pointer',
                            fontWeight: formData.price_type === p.id ? 600 : 400,
                          }}
                        >
                          {isArabic ? p.ar : p.en}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* iter288 — Special-case vehicle services: ask which
                      vehicle type the service is for, NOT brand/model. */}
                  {formData.service_category === 'vehicle_services' && (
                    <div className="row">
                      <label className="label">
                        {isArabic ? 'متخصص في (اختياري)' : 'Specializes in (optional)'}
                      </label>
                      <div className="chip-group" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {[
                          { id: 'cars',       en: 'Cars',       ar: 'سيارات' },
                          { id: 'motorbikes', en: 'Motorbikes', ar: 'دراجات نارية' },
                          { id: 'tuktuk',     en: 'Tuk-tuks',   ar: 'ركشات' },
                          { id: 'trucks',     en: 'Trucks',     ar: 'شاحنات' },
                          { id: 'all',        en: 'All',        ar: 'الكل' },
                        ].map(v => (
                          <button
                            key={v.id}
                            type="button"
                            className={`chip`}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              service_options: { ...(prev.service_options || {}), specializes_in: v.id },
                            }))}
                            data-testid={`service-specializes-${v.id}`}
                            style={{
                              border: '1px solid var(--border-color, #e5e7eb)',
                              background: formData.service_options?.specializes_in === v.id ? 'rgba(37,99,235,0.12)' : '#fff',
                              borderColor: formData.service_options?.specializes_in === v.id ? '#2563eb' : 'var(--border-color, #e5e7eb)',
                              color: formData.service_options?.specializes_in === v.id ? '#2563eb' : 'inherit',
                              borderRadius: 999,
                              padding: '6px 12px',
                              fontSize: 13,
                              cursor: 'pointer',
                              fontWeight: formData.service_options?.specializes_in === v.id ? 600 : 400,
                            }}
                          >
                            {isArabic ? v.ar : v.en}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        className="input"
                        style={{ marginTop: 8 }}
                        placeholder={isArabic ? 'تخصص العلامة التجارية اختياري (مثال: Toyota، Bajaj)' : 'Brand specialty optional (e.g. Toyota, Bajaj)'}
                        value={formData.service_options?.brand_specialty || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          service_options: { ...(prev.service_options || {}), brand_specialty: e.target.value },
                        }))}
                        data-testid="service-brand-specialty"
                        dir={isArabic ? 'rtl' : 'ltr'}
                      />
                    </div>
                  )}

                  {/* Experience years */}
                  <div className="row">
                    <label className="label">
                      {isArabic ? 'سنوات الخبرة' : 'Experience (years)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      className="input"
                      placeholder={isArabic ? 'مثال: 5' : 'e.g. 5'}
                      value={formData.service_options?.experience_years ?? ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        service_options: { ...(prev.service_options || {}), experience_years: e.target.value },
                      }))}
                      data-testid="service-experience-years"
                    />
                  </div>

                  {/* Yes/no toggles */}
                  <div className="row" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { id: 'home_visit', en: 'Home visit available', ar: 'يقدّم الخدمة بالمنزل' },
                      { id: 'emergency',  en: 'Emergency service',    ar: 'خدمة طوارئ' },
                    ].map(opt => (
                      <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={!!formData.service_options?.[opt.id]}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            service_options: { ...(prev.service_options || {}), [opt.id]: e.target.checked },
                          }))}
                          data-testid={`service-toggle-${opt.id}`}
                        />
                        <span>{isArabic ? opt.ar : opt.en}</span>
                      </label>
                    ))}
                  </div>

                  {/* Free text: service area + availability */}
                  <div className="row">
                    <label className="label">
                      {isArabic ? 'منطقة الخدمة (اختياري)' : 'Service area (optional)'}
                    </label>
                    <input
                      type="text"
                      className="input"
                      placeholder={isArabic ? 'مثال: الخرطوم، أم درمان' : 'e.g. Khartoum, Omdurman'}
                      value={formData.service_options?.service_area || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        service_options: { ...(prev.service_options || {}), service_area: e.target.value },
                      }))}
                      data-testid="service-service-area"
                      dir={isArabic ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div className="row">
                    <label className="label">
                      {isArabic ? 'أوقات العمل (اختياري)' : 'Availability (optional)'}
                    </label>
                    <input
                      type="text"
                      className="input"
                      placeholder={isArabic ? 'مثال: السبت-الخميس 8 صباحاً - 8 مساءً' : 'e.g. Sat-Thu 8am - 8pm'}
                      value={formData.service_options?.availability || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        service_options: { ...(prev.service_options || {}), availability: e.target.value },
                      }))}
                      data-testid="service-availability"
                      dir={isArabic ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>
              )}

              {/* Sticky Bottom Nav - Step 1 */}
              <div className="step-bottom-nav">
                <div className="step-nav-buttons single">
                  <button
                    onClick={() => handleValidatedNext(1, 2)}
                    className="nav-btn nav-btn-next"
                    data-testid="next-step-1"
                  >
                    {t('postAd.next')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Price, Currency & Location */}
          {step === 2 && (
            <div className="step-content">
              <div className="assistant-hint">
                <p>{t('assistantPriceTip')}</p>
              </div>

              {/* Section Header */}
              <div className="section-header">
                <PriceIcon />
                <span>{t('step2Title')}</span>
              </div>

              {/* Validation Alert */}
              {showTopAlert && step === 2 && (
                <div className="validation-top-alert" data-testid="validation-alert-step2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{isArabic ? 'يوجد حقول مطلوبة لم يتم إكمالها' : 'Some required fields are still missing'}</span>
                </div>
              )}

              <div className="row">
                <label className="label">{t('currencyLabel')} *</label>
                <button
                  type="button"
                  onClick={() => setShowCurrencyModal(true)}
                  className={`currency-select-trigger ${fieldErrors.currency ? 'has-error' : ''}`}
                  data-testid="currency-select-trigger"
                >
                  <span className="currency-select-value">
                    <span>{t(`currency.${formData.currency}`)}</span>
                    <span style={{ color: '#6B7280' }}>({formData.currency})</span>
                  </span>
                  <svg className="currency-select-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </div>

              <div className="price-row">
                <div className="row price-field">
                  <label className="label">{t('priceLabel')} *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleFieldChange('price', e.target.value)}
                    className={`input ${fieldErrors.price ? 'has-error' : ''}`}
                    placeholder={t('pricePlaceholder')}
                    min="0"
                    step="0.01"
                    data-testid="price-input"
                    dir={isArabic ? 'rtl' : 'ltr'}
                  />
                  {fieldErrors.price && (
                    <div className="field-error-msg" data-testid="price-error">{isArabic ? 'هذا الحقل مطلوب' : 'This field is required'}</div>
                  )}
                </div>
                <div className="row discount-field">
                  <label className="label">{t('discountLabel') || 'Discount %'}</label>
                  <input
                    type="number"
                    value={formData.discountPercent}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Clamp value between 0 and 100
                      if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                        handleFieldChange('discountPercent', val);
                      }
                    }}
                    className="input"
                    placeholder="0-100"
                    min="0"
                    max="100"
                    step="1"
                    data-testid="discount-input"
                  />
                </div>
              </div>

              <div className="row">
                <label className="label">{t('locationLabel')} *</label>
                {/* iter322 Phase B — Structured location is the canonical
                    source for `country` (required) + optionally `city`.
                    iter334 — City is no longer required for Next; it's
                    recommended but the seller can proceed with country
                    only (e.g. GPS that resolved a country but not a
                    clean city slug). */}
                <button
                  type="button"
                  className={`location-picker-trigger ${(!manualLocation?.country || !manualLocation?.region || !manualLocation?.city || !manualLocation?.area) && (fieldErrors.country || fieldErrors.region || fieldErrors.city || fieldErrors.area) ? 'has-error' : ''}`}
                  onClick={() => setLocationPickerOpen(true)}
                  data-testid="open-location-picker"
                  aria-label={t('locationSelector.title')}
                >
                  <span className="location-picker-trigger-icon">📍</span>
                  <span className="location-picker-trigger-text">
                    {manualLocation?.locationLabel ? (
                      <>
                        <strong>{manualLocation.locationLabel}</strong>
                        <span className="location-picker-trigger-change">
                          {t('postAd.change')}
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>{t('locationSelector.title')}</strong>
                        <span className="location-picker-trigger-hint">
                          {/* iter337 — Public-only safety helper + 4-level requirement. */}
                          {t('locationSelector.publicOnlyHelper')}
                        </span>
                      </>
                    )}
                  </span>
                </button>

                {(fieldErrors.country || fieldErrors.region || fieldErrors.city || fieldErrors.area) && (
                  <div className="field-error-msg" data-testid="location-required-error">
                    {/* iter337 — Single unified message per operator spec. */}
                    {t('locationSelector.completeRequired')}
                  </div>
                )}
                {fieldErrors.location_confirmed && !fieldErrors.country && !fieldErrors.region && !fieldErrors.city && !fieldErrors.area && (
                  <div className="field-error-msg" data-testid="location-unconfirmed-error">
                    {isArabic
                      ? 'يرجى تأكيد الموقع المختار قبل النشر.'
                      : 'Please confirm the selected location before publishing.'}
                  </div>
                )}

                {/* iter334 — Free-text city / town fallback. Optional.
                    Used for display only — strict filtering uses the
                    structured city from the picker when available. */}
                <label className="label" style={{ marginTop: '0.75rem' }}>
                  {isArabic ? 'اسم المدينة أو المنطقة' : 'City / town name'}
                </label>
                <input
                  type="text"
                  value={manualLocation?.manualCity || ''}
                  onChange={(e) => {
                    setManualLocation((p) => ({ ...p, manualCity: e.target.value }));
                    setDirty(true);
                  }}
                  className="input"
                  placeholder={isArabic
                    ? 'اختياري — مثال: ود مدني، الأبيض، عطبرة'
                    : 'Optional — e.g. Wad Medani, Sennar, Atbara'}
                  data-testid="location-manual-city-input"
                />

                {/* Optional landmark / extra details — display only,
                    NOT used for strict filtering. Kept so legacy ad
                    cards keep rendering useful hints ("near old
                    market", etc.). iter334 — copy tightened so sellers
                    know NOT to put their full address here. */}
                <label className="label" style={{ marginTop: '0.75rem' }}>
                  {isArabic
                    ? 'تفاصيل إضافية للموقع'
                    : 'Extra location details'}
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleFieldChange('location', e.target.value)}
                  className="input"
                  placeholder={isArabic
                    ? 'مثال: قرب السوق القديم، بجانب المسجد، اسم المحل'
                    : 'e.g. near old market, beside mosque, shop name'}
                  data-testid="location-input"
                />
                <div className="iter334-helper-text" data-testid="location-extra-helper">
                  {isArabic
                    ? 'تُستخدم فقط لمساعدة المشتري في الوصول إليك. لا تكتب عنوان المنزل الخاص بدقة.'
                    : 'Used only to help buyers find you. Do not write private home details.'}
                </div>
                <p className="helper-text" style={{ marginTop: '0.5rem' }}>
                  {isArabic
                    ? 'هذا الحقل اختياري للعرض فقط — تصفية الموقع تعتمد على الدولة والمدينة المختارة أعلاه.'
                    : 'This optional field is for display only — location filtering uses the country and city you picked above.'}
                </p>
              </div>

              {/* iter328 — Seller-provided Delivery option section.
                  After Location, before Photos/Review. NOT a Wassla-
                  managed courier; this is purely an availability hint
                  so buyers know whether the seller can deliver. */}
              <div className="row delivery-section" data-testid="delivery-section">
                <div className="delivery-section-header">
                  <span className="delivery-section-icon" aria-hidden="true">🚚</span>
                  <div>
                    <h3 className="delivery-section-title">
                      {isArabic ? 'هل توفر توصيل لهذا المنتج؟' : 'Do you offer delivery for this item?'}
                    </h3>
                    <p className="delivery-section-helper">
                      {isArabic
                        ? 'هذه معلومة من البائع — يتم ترتيب التوصيل مباشرة مع المشتري.'
                        : 'Seller-provided info — delivery is arranged directly with the buyer.'}
                    </p>
                  </div>
                </div>

                <div className="delivery-choice-grid" role="radiogroup" aria-label={isArabic ? 'خيار التوصيل' : 'Delivery option'}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!delivery.available}
                    className={`delivery-choice-card ${!delivery.available ? 'is-selected' : ''}`}
                    onClick={() => {
                      setDelivery({ ...EMPTY_DELIVERY });
                      setDirty(true);
                    }}
                    data-testid="delivery-choice-none"
                  >
                    <span className="delivery-choice-icon" aria-hidden="true">📦</span>
                    <div className="delivery-choice-body">
                      <strong>{isArabic ? 'لا يوجد توصيل' : 'No delivery'}</strong>
                      <span>{isArabic ? 'يستلم المشتري بنفسه' : 'Buyer collects'}</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    role="radio"
                    aria-checked={delivery.available}
                    className={`delivery-choice-card ${delivery.available ? 'is-selected' : ''}`}
                    onClick={() => {
                      setDelivery((prev) => ({
                        ...EMPTY_DELIVERY,
                        ...prev,
                        available: true,
                        type: prev.type && prev.type !== 'none' ? prev.type : 'free',
                      }));
                      setDirty(true);
                    }}
                    data-testid="delivery-choice-available"
                  >
                    <span className="delivery-choice-icon" aria-hidden="true">🚚</span>
                    <div className="delivery-choice-body">
                      <strong>{isArabic ? 'التوصيل متاح' : 'Delivery available'}</strong>
                      <span>{isArabic ? 'البائع يقوم بالتوصيل' : 'Seller can deliver'}</span>
                    </div>
                  </button>
                </div>

                {delivery.available && (
                  <div className="delivery-details" data-testid="delivery-details">
                    <label className="label">
                      {isArabic ? 'منطقة التوصيل (اختياري ولكن يُفضل)' : 'Delivery area (optional, recommended)'}
                    </label>
                    <input
                      type="text"
                      value={delivery.area}
                      onChange={(e) => { setDelivery((p) => ({ ...p, area: e.target.value })); setDirty(true); }}
                      className="input"
                      placeholder={isArabic
                        ? 'مثال: الخرطوم فقط، أم درمان، المناطق القريبة'
                        : 'Example: Khartoum only, Omdurman, nearby areas'}
                      data-testid="delivery-area-input"
                    />

                    <label className="label" style={{ marginTop: '0.85rem' }}>
                      {isArabic ? 'رسوم التوصيل' : 'Delivery fee'}
                    </label>
                    <div className="delivery-fee-grid" role="radiogroup">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={delivery.type === 'free'}
                        className={`delivery-fee-pill ${delivery.type === 'free' ? 'is-selected' : ''}`}
                        onClick={() => { setDelivery((p) => ({ ...p, type: 'free', fee_amount: '' })); setDirty(true); }}
                        data-testid="delivery-fee-free"
                      >
                        {isArabic ? 'توصيل مجاني' : 'Free delivery'}
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={delivery.type === 'paid'}
                        className={`delivery-fee-pill ${delivery.type === 'paid' ? 'is-selected' : ''}`}
                        onClick={() => { setDelivery((p) => ({ ...p, type: 'paid' })); setDirty(true); }}
                        data-testid="delivery-fee-paid"
                      >
                        {isArabic ? 'توصيل برسوم' : 'Paid delivery'}
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={delivery.type === 'depends_on_location'}
                        className={`delivery-fee-pill ${delivery.type === 'depends_on_location' ? 'is-selected' : ''}`}
                        onClick={() => { setDelivery((p) => ({ ...p, type: 'depends_on_location', fee_amount: '' })); setDirty(true); }}
                        data-testid="delivery-fee-depends"
                      >
                        {isArabic ? 'الرسوم حسب الموقع' : 'Fee depends on location'}
                      </button>
                    </div>

                    {delivery.type === 'paid' && (
                      <div className="delivery-paid-fields" data-testid="delivery-paid-fields">
                        <div className="delivery-paid-row">
                          <div className="delivery-paid-amount">
                            <label className="label">
                              {isArabic ? 'مبلغ التوصيل' : 'Delivery fee amount'} *
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={delivery.fee_amount}
                              onChange={(e) => { setDelivery((p) => ({ ...p, fee_amount: e.target.value })); setDirty(true); }}
                              className={`input ${fieldErrors.delivery_fee_amount ? 'input-error' : ''}`}
                              placeholder={isArabic ? 'مثال: 2000' : 'e.g. 2000'}
                              data-testid="delivery-fee-amount-input"
                            />
                          </div>
                          <div className="delivery-paid-currency">
                            <label className="label">
                              {isArabic ? 'العملة' : 'Currency'} *
                            </label>
                            {/* iter334 — Premium picker (bottom sheet) replaces
                                the native <select>. Tappable pill shows the
                                current code + flag; the sheet is opened via
                                the `deliveryCurrencySheetOpen` state. */}
                            <button
                              type="button"
                              onClick={() => setDeliveryCurrencySheetOpen(true)}
                              className={`iter334-currency-pill ${fieldErrors.delivery_fee_currency ? 'has-error' : ''}`}
                              data-testid="delivery-fee-currency-button"
                              aria-label={isArabic ? 'اختر العملة' : 'Choose currency'}
                            >
                              <span className="iter334-currency-pill-flag" aria-hidden="true">
                                {deliveryCurrencyOptions.find((o) => o.code === delivery.fee_currency)?.flag || '🏳️'}
                              </span>
                              <span className="iter334-currency-pill-code">
                                {delivery.fee_currency || 'SDG'}
                              </span>
                              <span className="iter334-currency-pill-name">
                                {isArabic
                                  ? (deliveryCurrencyOptions.find((o) => o.code === delivery.fee_currency)?.name_ar || '')
                                  : (deliveryCurrencyOptions.find((o) => o.code === delivery.fee_currency)?.name_en || '')}
                              </span>
                              <span className="iter334-currency-pill-chev" aria-hidden="true">▾</span>
                            </button>
                          </div>
                        </div>
                        {(fieldErrors.delivery_fee_amount || fieldErrors.delivery_fee_currency) && (
                          <div className="field-error-msg" data-testid="delivery-fee-error">
                            {isArabic
                              ? 'يرجى تحديد مبلغ التوصيل والعملة.'
                              : 'Please enter the delivery fee amount and currency.'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sticky Bottom Nav - Step 2 */}
              <div className="step-bottom-nav">
                <div className="step-nav-buttons">
                  <button
                    onClick={() => setStep(1)}
                    className="nav-btn nav-btn-back"
                    data-testid="back-step-2"
                  >
                    {t('postAd.back')}
                  </button>
                  <button
                    onClick={() => {
                      handleValidatedNext(2, 3);
                    }}
                    className="nav-btn nav-btn-next"
                    data-testid="next-step-2"
                  >
                    {t('postAd.next')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Description & Images */}
          {step === 3 && (
            <div className="step-content">
              <div className="assistant-hint">
                <p>{t('assistantPhotoTip')}</p>
              </div>

              {/* Section Header */}
              <div className="section-header">
                <span>{t('step3Title')}</span>
              </div>

              {/* Validation Alert */}
              {showTopAlert && step === 3 && (
                <div className="validation-top-alert" data-testid="validation-alert-step3">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{isArabic ? 'يوجد حقول مطلوبة لم يتم إكمالها' : 'Some required fields are still missing'}</span>
                </div>
              )}

              <div className="row">
                <label className="label">{t('descLabel')} *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  className={`input ${fieldErrors.description ? 'has-error' : ''}`}
                  rows="4"
                  placeholder={t('descPlaceholder')}
                  data-testid="description-input"
                  style={{ minHeight: '110px', resize: 'vertical' }}
                  dir={isArabic ? 'rtl' : 'ltr'}
                />
                {fieldErrors.description && (
                  <div className="field-error-msg" data-testid="desc-error">{isArabic ? 'هذا الحقل مطلوب' : 'This field is required'}</div>
                )}
                <p className="helper-text" style={{ marginTop: '0.5rem' }}>
                  {t('descHelper')}
                </p>
                {formData.category && (
                  <p className="helper-text-example" dir={isArabic ? 'rtl' : 'ltr'} style={{ 
                    marginTop: '0.5rem', 
                    fontStyle: 'italic',
                    backgroundColor: '#f0f9ff',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    borderLeft: isArabic ? 'none' : '3px solid #3b82f6',
                    borderRight: isArabic ? '3px solid #3b82f6' : 'none',
                    textAlign: isArabic ? 'right' : 'left'
                  }}>
                    <strong style={{ display: 'block', marginBottom: '0.25rem' }}>
                      {t('postAd.exampleLabel')}
                    </strong>
                    {getDescriptionExample()}
                  </p>
                )}
              </div>

              <div className="row">
                <label className="label">{t('imagesTitle')} ({t('imagesMax')})</label>
                <div className="photo-guidelines">
                  <p>📸 {t('imagesQualityTip')}</p>
                </div>
                
                {/* Dashed upload box - always visible */}
                <div className={`upload-box ${selectedImages.length >= 5 ? 'disabled' : ''}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="file-input-hidden"
                    id="image-upload-input"
                    data-testid="image-upload"
                    disabled={selectedImages.length >= 5}
                  />
                  <label htmlFor="image-upload-input" className="upload-box-label">
                    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>
                      {selectedImages.length >= 5 
                        ? (t('postAd.maxImages'))
                        : selectedImages.length > 0
                          ? (t('postAd.photosSelected', { count: selectedImages.length }))
                          : (t('postAd.choosePhotos'))
                      }
                    </span>
                    <span className="upload-hint">
                      {t('postAd.maxPhotos')}
                    </span>
                  </label>
                </div>

                {/* Photo guidance tips */}
                <div className="photo-guidance-tips" data-testid="photo-guidance-tips">
                  <div className="photo-guidance-title">{t('postAd.photoGuidanceTitle')}</div>
                  <ul className="photo-guidance-list">
                    <li>✓ {t('postAd.photoGuidanceTip1')}</li>
                    <li>✓ {t('postAd.photoGuidanceTip2')}</li>
                    <li>✓ {t('postAd.photoGuidanceTip3')}</li>
                    <li>✗ {t('postAd.photoGuidanceTip4')}</li>
                  </ul>
                </div>
                
                {uploading && <p className="upload-status">{t('uploading')}</p>}
                
                {/* Image preview grid with quality badges and reorder */}
                {selectedImages.length > 0 && (
                  <>
                    <div className="cover-image-hint" data-testid="cover-image-hint">
                      {t('postAd.coverImageHint')}
                    </div>
                    <div className="image-preview-grid">
                      {selectedImages.map((img, idx) => (
                        <div key={idx} className={`preview-item ${img.qualityIssue ? 'has-quality-issue' : ''} ${idx === 0 ? 'is-cover' : ''}`}>
                          <img src={img.previewUrl} alt={`Preview ${idx + 1}`} className="preview-img" />
                          
                          {/* Cover badge for first image */}
                          {idx === 0 && (
                            <div className="cover-badge">{isArabic ? 'الغلاف' : 'Cover'}</div>
                          )}
                          
                          {/* Quality warning badge */}
                          {img.qualityIssue && (
                            <div className="quality-warning-badge" title={t('imageQualityWarning')}>
                              ⚠️
                              <span className="quality-issue-text">
                                {img.isBlurry ? t('imageBlurry') : ''}
                                {img.isBlurry && img.isDark ? ' / ' : ''}
                                {img.isDark ? t('imageDark') : ''}
                              </span>
                            </div>
                          )}
                          
                          {/* Reorder buttons */}
                          <div className="reorder-buttons">
                            {idx > 0 && (
                              <button
                                type="button"
                                onClick={() => moveImageUp(idx)}
                                className="reorder-btn reorder-up"
                                aria-label={t('postAd.moveUp')}
                                data-testid={`move-up-${idx}`}
                              >
                                ↑
                              </button>
                            )}
                            {idx < selectedImages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveImageDown(idx)}
                                className="reorder-btn reorder-down"
                                aria-label={t('postAd.moveDown')}
                                data-testid={`move-down-${idx}`}
                              >
                                ↓
                              </button>
                            )}
                          </div>
                          
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => removeSelectedImage(idx)}
                            className="remove-image-btn"
                            aria-label={t('delete')}
                            data-testid={`remove-image-${idx}`}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Quality warning summary */}
                {getLowQualityImageCount() > 0 && (
                  <p className="quality-warning-summary">
                    ⚠️ {t('imageQualityWarning')}
                  </p>
                )}
              </div>

              {/* Seller Contact Information Section */}
              <div className="contact-fields-section">
                <div className="section-header contact-section-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  {/* iter314 — Section is required, drop "(Optional)" from the title. */}
                  <span data-testid="contact-info-title">{t('postAd.contactInfoTitle')}</span>
                </div>

                <p className="contact-fields-helper" data-testid="contact-info-helper">
                  {t('postAd.contactInfoRequiredHelper')}
                </p>

                {/* iter314 — Country-neutral inline validation banner.
                    The wording is split into two reasons so we never
                    tell a seller to "enter a valid Sudanese number"
                    when the field is just empty, or when they're
                    publishing from outside Sudan. */}
                {fieldErrors.seller_contact && (
                  <div
                    className="contact-validation-banner has-error"
                    role="alert"
                    data-testid="seller-contact-error"
                    data-error-reason={fieldErrors.seller_contact}
                    style={{
                      background: '#FEF2F2',
                      border: '1px solid #FCA5A5',
                      color: '#991B1B',
                      borderRadius: 8,
                      padding: '10px 12px',
                      fontSize: 13,
                      margin: '8px 0 12px',
                      lineHeight: 1.5,
                    }}
                  >
                    {fieldErrors.seller_contact === 'empty'
                      ? t('postAd.contactRequiredEmpty')
                      : t('postAd.contactRequiredInvalid')}
                  </div>
                )}

                {/* Phone Number — iter334 split input (country code pill + local) */}
                <div className="row">
                  <label className="label">{t('postAd.phoneNumber')}</label>
                  <PhoneInputSplit
                    countryIso2={manualLocation?.country || 'SD'}
                    countriesMeta={countriesMeta}
                    value={{
                      country_code: formData.phone_country_code || '',
                      local: formData.phone_local || '',
                    }}
                    onChange={(next) => {
                      handleFieldChange('phone_country_code', next.country_code || '');
                      handleFieldChange('phone_local', next.local || '');
                      // Mirror into `seller_phone` so legacy save / WhatsApp
                      // toggle paths keep working. Compute a best-effort
                      // E.164 here; backend re-validates on submit.
                      const cc = (next.country_code || '').startsWith('+')
                        ? next.country_code
                        : `+${next.country_code || ''}`;
                      const local = (next.local || '').replace(/\D/g, '').replace(/^0+/, '');
                      const synth = cc && local ? `${cc}${local}` : '';
                      handleFieldChange('seller_phone', synth);
                      if (formData.use_same_for_whatsapp) {
                        handleFieldChange('seller_whatsapp', synth);
                      }
                    }}
                    placeholder={isArabic ? 'مثال: 912345678' : 'e.g. 912345678'}
                    error={!!(fieldErrors.phone_split || fieldErrors.seller_phone)}
                    errorMessage={
                      isArabic
                        ? 'أدخل رقم هاتف صحيح. رمز الدولة محدد بالفعل.'
                        : 'Enter a valid phone number. Country code is already selected.'
                    }
                    isRTL={isArabic}
                    testId="seller-phone-split"
                  />
                </div>

                {/* WhatsApp Toggle */}
                {formData.seller_phone && (
                  <div className="whatsapp-toggle-row">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.use_same_for_whatsapp}
                        onChange={(e) => {
                          handleFieldChange('use_same_for_whatsapp', e.target.checked);
                          if (e.target.checked) {
                            handleFieldChange('seller_whatsapp', formData.seller_phone);
                          } else {
                            handleFieldChange('seller_whatsapp', '');
                          }
                        }}
                        className="toggle-checkbox"
                        data-testid="whatsapp-same-toggle"
                      />
                      <span className="toggle-text">
                        {t('postAd.useSameForWhatsapp')}
                      </span>
                    </label>
                  </div>
                )}

                {/* WhatsApp (only show if different from phone) */}
                {formData.seller_phone && !formData.use_same_for_whatsapp && (
                  <div className="row">
                    <label className="label">{t('postAd.whatsappNumber')}</label>
                    <input
                      type="tel"
                      value={formData.seller_whatsapp}
                      onChange={(e) => handleFieldChange('seller_whatsapp', e.target.value)}
                      className="input"
                      placeholder={t('postAd.whatsappDiffPlaceholder')}
                      data-testid="seller-whatsapp-input"
                      dir="ltr"
                    />
                  </div>
                )}

                {/* Messenger Link */}
                <div className="row">
                  <label className="label">
                    {t('postAd.messengerLinkOptional')}
                  </label>
                  <input
                    type="text"
                    value={formData.seller_messenger}
                    onChange={(e) => handleFieldChange('seller_messenger', e.target.value)}
                    className="input"
                    placeholder={t('postAd.messengerPlaceholder')}
                    data-testid="seller-messenger-input"
                    dir="ltr"
                  />
                  <p className="helper-text">
                    {t('postAd.messengerExample')}
                  </p>
                </div>

                {/* Email */}
                <div className="row">
                  <label className="label">{t('postAd.emailOptional')}</label>
                  <input
                    type="email"
                    value={formData.seller_email}
                    onChange={(e) => handleFieldChange('seller_email', e.target.value)}
                    className="input"
                    placeholder="example@email.com"
                    data-testid="seller-email-input"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Sticky Bottom Nav - Step 3 */}
              <div className="step-bottom-nav">
                <div className="step-nav-buttons">
                  <button
                    onClick={() => setStep(2)}
                    className="nav-btn nav-btn-back"
                    data-testid="back-step-3"
                  >
                    {t('postAd.back')}
                  </button>
                  <button
                    onClick={() => handleValidatedNext(3, 4)}
                    className="nav-btn nav-btn-next"
                    data-testid="preview-button"
                  >
                    {t('postAd.preview')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Preview Before Publishing */}
          {step === 4 && (
            <div className="step-content">
              {/* Section Header */}
              <div className="section-header preview-section-header">
                <PreviewIcon />
                <span>{t('step4Title')}</span>
              </div>

              <div className="preview-header">
                <p className="preview-subtitle">{t('previewHint')}</p>
              </div>

              <div className="preview-card" data-testid="preview-card">
                {/* Preview Images Section - Using deduplicated images */}
                <div className="preview-images" data-testid="preview-images">
                  {uniquePreviewImages.length > 0 ? (
                    <>
                      {/* Get the preview URL - handle both previewUrl and direct URL */}
                      {(() => {
                        const safeIndex = Math.min(previewImageIndex || 0, uniquePreviewImages.length - 1);
                        const currentImg = uniquePreviewImages[safeIndex];
                        const imgSrc = currentImg?.previewUrl || currentImg?.url || currentImg;
                        return imgSrc ? (
                          <img 
                            src={typeof imgSrc === 'string' ? imgSrc : ''}
                            alt={t('postAd.preview')}
                            className="preview-main-image"
                            data-testid="preview-main-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              e.target.style.display = 'block';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'none';
                            }}
                          />
                        ) : null;
                      })()}
                      {/* Skeleton fallback - NO text, just shimmer animation */}
                      <div className="preview-image-fallback" style={{ display: uniquePreviewImages[Math.min(previewImageIndex || 0, uniquePreviewImages.length - 1)]?.previewUrl ? 'none' : 'flex' }}>
                        <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path d="M4 16l4-4a3 3 0 014.8.8M14 8h.01M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      {uniquePreviewImages.length > 1 && (
                        <>
                          <div className="preview-image-count" data-testid="image-count">
                            {(previewImageIndex || 0) + 1} / {uniquePreviewImages.length}
                          </div>
                          <div className="preview-nav-buttons">
                            <button 
                              className="preview-nav-btn prev-btn"
                              onClick={() => setPreviewImageIndex(prev => prev > 0 ? prev - 1 : uniquePreviewImages.length - 1)}
                              aria-label={t('postAd.back')}
                              data-testid="prev-image-btn"
                            >
                              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <button 
                              className="preview-nav-btn next-btn"
                              onClick={() => setPreviewImageIndex(prev => prev < uniquePreviewImages.length - 1 ? prev + 1 : 0)}
                              aria-label={t('postAd.next')}
                              data-testid="next-image-btn"
                            >
                              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="preview-no-image" data-testid="no-images-message">
                      <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M4 16l4-4a3 3 0 014.8.8M14 8h.01M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{t('postAd.noImagesAdded')}</span>
                    </div>
                  )}
                </div>
                
                {/* Image Thumbnails - Using deduplicated images */}
                {uniquePreviewImages.length > 0 && (
                  <div className="preview-thumbnails" data-testid="preview-thumbnails">
                    {uniquePreviewImages.map((img, idx) => {
                      const thumbSrc = img?.previewUrl || img?.url || img;
                      return (
                        <button 
                          key={`thumb-${idx}-${thumbSrc?.slice?.(-10) || idx}`}
                          className={`preview-thumbnail ${(previewImageIndex || 0) === idx ? 'active' : ''}`}
                          onClick={() => setPreviewImageIndex(idx)}
                          data-testid={`thumbnail-${idx}`}
                          aria-label={t('postAd.viewImage', { num: idx + 1 })}
                        >
                          {typeof thumbSrc === 'string' && thumbSrc ? (
                            <>
                              <img 
                                src={thumbSrc} 
                                alt=""
                                onError={(e) => {
                                  // Hide broken image completely, show placeholder
                                  e.target.style.display = 'none';
                                  const placeholder = e.target.parentElement.querySelector('.thumb-placeholder');
                                  if (placeholder) placeholder.style.display = 'flex';
                                }}
                              />
                              <div className="thumb-placeholder" style={{ display: 'none' }}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                  <path d="M4 16l4-4a3 3 0 014.8.8M14 8h.01M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <div className="thumb-placeholder">
                              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                <path d="M4 16l4-4a3 3 0 014.8.8M14 8h.01M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {/* Quality warning indicator on thumbnail */}
                          {img?.qualityIssue && (
                            <span className="thumbnail-warning">⚠️</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Image Count Label */}
                {uniquePreviewImages.length > 0 && (
                  <div className="preview-images-label" data-testid="images-count-label">
                    <span>{t('postAd.images')}</span>
                    <span className="images-count">{uniquePreviewImages.length} / 5</span>
                  </div>
                )}

                <div className="preview-info">
                  <h3 className="preview-ad-title">{formData.title}</h3>
                  <div className="preview-price-row">
                    <p className="preview-ad-price">{formatMoney(parseFloat(formData.price) || 0, { locale: isArabic ? 'ar-SD' : 'en', currency: formData.currency || 'SDG' })}</p>
                    {formData.discountPercent && Number(formData.discountPercent) > 0 && (
                      <span className="preview-discount-badge" data-testid="preview-discount-badge">
                        -{formData.discountPercent}%
                      </span>
                    )}
                  </div>
                  <div className="preview-ad-location">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{formData.location}</span>
                  </div>
                  <p className="preview-ad-description">{formData.description}</p>

                  {/* Vertical Attributes Preview */}
                  {verticalAttrs.phone_attrs && Object.values(verticalAttrs.phone_attrs).some(Boolean) && (
                    <div className="preview-attrs-section" data-testid="preview-phone-attrs">
                      <h4 className="preview-attrs-title">{t('postAd.phoneDetails')}</h4>
                      <div className="preview-attrs-grid">
                        {verticalAttrs.phone_attrs.phone_brand && <span className="preview-attr-pill">{verticalAttrs.phone_attrs.phone_brand}</span>}
                        {verticalAttrs.phone_attrs.phone_model && <span className="preview-attr-pill">{verticalAttrs.phone_attrs.phone_model}</span>}
                        {verticalAttrs.phone_attrs.storage && <span className="preview-attr-pill">{verticalAttrs.phone_attrs.storage}</span>}
                        {verticalAttrs.phone_attrs.condition && <span className="preview-attr-pill">{displayVal(verticalAttrs.phone_attrs.condition)}</span>}
                        {verticalAttrs.phone_attrs.battery_health && <span className="preview-attr-pill">🔋 {verticalAttrs.phone_attrs.battery_health}%</span>}
                      </div>
                    </div>
                  )}
                  {verticalAttrs.car_attrs && Object.values(verticalAttrs.car_attrs).some(Boolean) && (
                    <div className="preview-attrs-section" data-testid="preview-car-attrs">
                      <h4 className="preview-attrs-title">{t('postAd.carDetails')}</h4>
                      <div className="preview-attrs-grid">
                        {verticalAttrs.car_attrs.brand && <span className="preview-attr-pill">{verticalAttrs.car_attrs.brand}</span>}
                        {verticalAttrs.car_attrs.model && <span className="preview-attr-pill">{verticalAttrs.car_attrs.model}</span>}
                        {verticalAttrs.car_attrs.year && <span className="preview-attr-pill">{verticalAttrs.car_attrs.year}</span>}
                        {verticalAttrs.car_attrs.fuel && <span className="preview-attr-pill">{displayVal(verticalAttrs.car_attrs.fuel)}</span>}
                        {verticalAttrs.car_attrs.transmission && <span className="preview-attr-pill">{displayVal(verticalAttrs.car_attrs.transmission)}</span>}
                        {verticalAttrs.car_attrs.mileage && <span className="preview-attr-pill">{verticalAttrs.car_attrs.mileage.toLocaleString()} {isArabic ? 'كم' : 'km'}</span>}
                        {verticalAttrs.car_attrs.condition && <span className="preview-attr-pill">{displayVal(verticalAttrs.car_attrs.condition)}</span>}
                        {verticalAttrs.car_attrs.body_type && <span className="preview-attr-pill">{displayVal(verticalAttrs.car_attrs.body_type)}</span>}
                      </div>
                    </div>
                  )}
                  {verticalAttrs.property_attrs && Object.values(verticalAttrs.property_attrs).some(Boolean) && (
                    <div className="preview-attrs-section" data-testid="preview-property-attrs">
                      <h4 className="preview-attrs-title">{t('postAd.propertyDetails')}</h4>
                      <div className="preview-attrs-grid">
                        {verticalAttrs.property_attrs.property_type && <span className="preview-attr-pill">{displayVal(verticalAttrs.property_attrs.property_type)}</span>}
                        {verticalAttrs.property_attrs.bedrooms && <span className="preview-attr-pill">{verticalAttrs.property_attrs.bedrooms} {t('postAd.beds')}</span>}
                        {verticalAttrs.property_attrs.bathrooms && <span className="preview-attr-pill">{verticalAttrs.property_attrs.bathrooms} {t('postAd.baths')}</span>}
                        {verticalAttrs.property_attrs.area_size && <span className="preview-attr-pill">{verticalAttrs.property_attrs.area_size} {isArabic ? 'م²' : 'm²'}</span>}
                      </div>
                    </div>
                  )}

                  {/* iter268: explicit Selected-Category review block.
                      Shows the user EXACTLY what will be saved before
                      they hit Publish. Renders parent + subcategory side
                      by side so a vehicle ad with a "Cars" sub is
                      unambiguous — "Vehicles & Motorbikes · Cars" — and
                      can't be misread as just "Cars". */}
                  <div
                    className="preview-category-review"
                    data-testid="preview-category-review"
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      background: 'rgba(34, 197, 94, 0.06)',
                      borderInlineStart: '3px solid #22c55e',
                      borderRadius: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: '#16a34a',
                        marginBottom: 4,
                      }}
                    >
                      {isArabic ? 'الفئة المختارة' : 'Selected category'}
                    </div>
                    <div
                      style={{ fontWeight: 600, fontSize: 14 }}
                      data-testid="preview-category-parent"
                    >
                      {formData.category === 'other'
                        ? (formData.category_other || (isArabic ? 'أخرى' : 'Other'))
                        : t(`cat.${formData.category}`)}
                      {formData.category_sub && (
                        <span
                          style={{ color: 'var(--admin-text-dim)', fontWeight: 500 }}
                          data-testid="preview-category-sub"
                        >
                          {' · '}
                          {(() => {
                            // Try i18n key first, fall back to humanised id.
                            const subT = t(`cat.${formData.category_sub}`);
                            return subT && !subT.startsWith('cat.')
                              ? subT
                              : formData.category_sub.replace(/_/g, ' ');
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Checklist */}
              <div className="quality-checklist" data-testid="quality-checklist">
                <div className="quality-checklist-title">{t('postAd.listingQuality')}</div>
                <div className="quality-checklist-items">
                  <span className={formData.title?.length >= 12 ? 'qc-pass' : 'qc-fail'}>
                    {formData.title?.length >= 12 ? '✓' : '○'} {t('postAd.clearTitle')}
                  </span>
                  <span className={formData.description?.length >= 40 ? 'qc-pass' : 'qc-fail'}>
                    {formData.description?.length >= 40 ? '✓' : '○'} {t('postAd.goodDescription')}
                  </span>
                  <span className={selectedImages.length >= 3 ? 'qc-pass' : selectedImages.length >= 1 ? 'qc-warn' : 'qc-fail'}>
                    {selectedImages.length >= 3 ? '✓' : selectedImages.length >= 1 ? '△' : '○'} {t('postAd.threePhotos')}
                  </span>
                  <span className={(manualLocation?.country && manualLocation?.region && manualLocation?.city && manualLocation?.area) ? 'qc-pass' : 'qc-fail'} data-testid="qc-location">
                    {(manualLocation?.country && manualLocation?.region && manualLocation?.city && manualLocation?.area) ? '✓' : '○'} {t('postAd.locationCheck')}
                  </span>
                  <span className={formData.price ? 'qc-pass' : 'qc-fail'}>
                    {formData.price ? '✓' : '○'} {t('postAd.priceCheck')}
                  </span>
                </div>
              </div>

              {/* Visibility Hint */}
              <div className="visibility-hint" data-testid="visibility-hint">
                <span className="visibility-hint-icon">💡</span>
                <span>{selectedImages.length < 3 ? t('postAd.visibilityHint') : t('postAd.visibilityHintTitle')}</span>
              </div>

              {/* Sticky Bottom Nav - Step 4 (Preview) with 3 buttons */}
              <div className="preview-bottom-actions">
                {/* Primary row: Publish button full width */}
                <button
                  onClick={handleSubmit}
                  disabled={loading || uploading}
                  className="preview-btn-publish"
                  data-testid="publish-button"
                >
                  {loading || uploading 
                    ? (t('postAd.publishing')) 
                    : (t('postAd.publish'))}
                </button>
                
                {/* Secondary row: Edit + Save as draft */}
                <div className="preview-secondary-actions">
                  <button
                    onClick={() => setStep(3)}
                    className="preview-btn-secondary"
                    data-testid="edit-listing"
                  >
                    {t('postAd.editBtn')}
                  </button>
                  <button
                    onClick={async () => {
                      await saveDraft(true);
                    }}
                    disabled={savingDraft}
                    className="preview-btn-secondary"
                    data-testid="save-draft-preview"
                  >
                    {savingDraft 
                      ? (t('postAd.savingDraft')) 
                      : (t('postAd.saveAsDraft'))}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Exit Confirmation Dialog - Premium Design with Save Draft option */}
      {showExitDialog && (
        <div className="exit-dialog-overlay" onClick={() => setShowExitDialog(false)}>
          <div className="exit-dialog premium-exit-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Premium Icon Header */}
            <div className="exit-dialog-icon">
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
              </svg>
            </div>
            
            <h3 className="exit-dialog-title">
              {t('postAd.saveDraftTitle')}
            </h3>
            <p className="exit-dialog-message">
              {t('postAd.saveDraftDesc')}
            </p>
            <div className="exit-dialog-buttons-stack">
              {/* Save as Draft - Primary */}
              <button
                onClick={async () => {
                  const success = await saveDraft(false);
                  if (success) {
                    setShowExitDialog(false);
                    if (hasInAppHistory()) navigate(-1);
                    else navigate(isLadiesHub ? '/ladies' : '/', { replace: true });
                  }
                }}
                disabled={savingDraft}
                className="exit-btn exit-btn-primary"
                data-testid="save-draft"
              >
                {savingDraft ? (
                  <>
                    <svg className="animate-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('postAd.savingDraft')}
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                    {t('postAd.saveDraftBtn')}
                  </>
                )}
              </button>
              
              {/* Continue Editing - Secondary */}
              <button
                onClick={() => setShowExitDialog(false)}
                className="exit-btn exit-btn-secondary"
                data-testid="cancel-exit"
              >
                {t('postAd.continueEditing')}
              </button>
              
              {/* Leave Without Saving - Destructive */}
              <button
                onClick={confirmExit}
                className="exit-btn exit-btn-destructive"
                data-testid="confirm-exit"
              >
                {t('postAd.leaveWithoutSaving')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Currency Selection Modal */}
      {showCurrencyModal && (
        <div className="currency-modal-overlay" onClick={() => setShowCurrencyModal(false)}>
          <div className="currency-modal-content" onClick={(e) => e.stopPropagation()}>
            <CurrencySelect
              value={formData.currency}
              onChange={(code) => {
                handleFieldChange('currency', code);
                setShowCurrencyModal(false);
              }}
              onClose={() => setShowCurrencyModal(false)}
            />
          </div>
        </div>
      )}

      {/* iter334 — Premium delivery-fee currency picker (bottom sheet) */}
      <CurrencyBottomSheet
        open={deliveryCurrencySheetOpen}
        onClose={() => setDeliveryCurrencySheetOpen(false)}
        onSelect={(item) => {
          setDelivery((p) => ({ ...p, fee_currency: item.code }));
          setDirty(true);
        }}
        items={deliveryCurrencyOptions}
        title={isArabic ? 'اختر عملة التوصيل' : 'Choose delivery fee currency'}
        searchPlaceholder={isArabic ? 'بحث' : 'Search'}
        selectedCode={delivery.fee_currency}
        mode="currency"
        isRTL={isArabic}
      />

      {/* Location Permission Pre-Modal (Arabic/English) */}
      {showLocationPermissionModal && (
        <div className="location-permission-overlay" onClick={handleLocationPermissionCancel}>
          <div className="location-permission-modal" onClick={(e) => e.stopPropagation()}>
            <div className="permission-icon">📍</div>
            <h3 className="permission-title">
              {t('postAd.allowLocation')}
            </h3>
            <p className="permission-body">
              {t('postAd.locationRationale')}
            </p>
            <div className="permission-buttons">
              <button
                type="button"
                onClick={handleLocationPermissionCancel}
                className="btn-ghost-full"
                data-testid="location-permission-cancel"
              >
                {t('postAd.notNow')}
              </button>
              <button
                type="button"
                onClick={handleLocationPermissionContinue}
                className="btn-primary-full"
                data-testid="location-permission-continue"
              >
                {t('postAd.continueBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* iter322 Phase B — Premium Location Picker (Country → Region → City → Area).
          Replaces the legacy free-form location text input as the canonical
          source for country + city. Apply (locationConfirmed=true) is
          required before Step 2 can advance to Step 3. */}
      <LocationPickerSheet
        open={locationPickerOpen}
        value={manualLocation}
        onApply={(loc) => {
          const countryNode = getCountry(loc?.country);
          setManualLocation({
            country: loc?.country || '',
            country_name: countryNode ? countryNode.countryName : (loc?.country_name || ''),
            region: loc?.region || '',
            city: loc?.city || '',
            area: loc?.area || '',
            // iter337 — Level-5 optional free-text from the picker.
            extra: loc?.extra || '',
            latitude: loc?.latitude ?? null,
            longitude: loc?.longitude ?? null,
            locationSource: loc?.locationSource || 'manual',
            locationConfirmed: Boolean(loc?.locationConfirmed),
            locationLabel: loc?.locationLabel || '',
          });
          setDirty(true);
          setLocationPickerOpen(false);
          // iter337 — Clear all 4-level + confirmed validation errors
          // now that the seller has selected and Applied a location.
          if (
            fieldErrors.country || fieldErrors.region ||
            fieldErrors.city || fieldErrors.area ||
            fieldErrors.location_confirmed
          ) {
            setFieldErrors((prev) => {
              const next = { ...prev };
              delete next.country;
              delete next.region;
              delete next.city;
              delete next.area;
              delete next.location_confirmed;
              return next;
            });
          }
        }}
        onClose={() => setLocationPickerOpen(false)}
        onClear={() => {
          setManualLocation(EMPTY_MANUAL_LOC);
          setDirty(true);
        }}
        testid="post-ad-location-picker"
      />

      </div>{/* End add-ad-content */}
    </div>
  );
}

export default AddAd;
